import React, { useContext, useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext, API, getAuthHeaders, formatApiError } from '../App';
import { Navigation } from '../components/Navigation';
import { NotificationPrompt } from '../components/NotificationPrompt';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import CourseSearchInput from '../components/CourseSearchInput';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import {
  Trophy, Target, DollarSign, Calendar, PlusCircle,
  TrendingUp, Users, MapPin, Clock, Send, Loader2, Bell, Flag, Search, Mail, UserPlus, Plane, MessageCircle, Tv, Camera, Rss,
  ChevronRight, Flame, ThumbsUp, MessageSquare, Share2, Crown, X, Video, Eye, Sparkles, Heart, Play, ExternalLink, Briefcase, Tag, User, ClipboardEdit
} from 'lucide-react';
import { toast } from 'sonner';
import ScorecardScanner from '../components/ScorecardScanner';
import { BundleTagsInfoCard } from '../components/BundleTagsInfoCard';
import NewsHub from '../components/NewsHub';
import TrendingThisWeek from '../components/TrendingThisWeek';
import DashboardFeedPreview from '../components/DashboardFeedPreview';

const formatTimeAgo = (dateStr) => {
  if (!dateStr) return '';
  const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const getGreeting = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
};




export default function Dashboard() {
  const { user, loading, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [stats, setStats] = useState(null);
  const [recentStories, setRecentStories] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [lookingForPlayers, setLookingForPlayers] = useState([]);
  const [promoVideos, setPromoVideos] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [openRounds, setOpenRounds] = useState([]);
  const [joiningRound, setJoiningRound] = useState(null);
  const [showLFPModal, setShowLFPModal] = useState(false);
  const [lfpForm, setLfpForm] = useState({ course_name: '', date: '', time: '', spots_needed: '1', message: '' });
  const [postingLFP, setPostingLFP] = useState(false);
  const [liveRoundFeed, setLiveRoundFeed] = useState([]);
  const [myActiveRound, setMyActiveRound] = useState(null);
  const [showStartRound, setShowStartRound] = useState(false);
  const [linkedEventId, setLinkedEventId] = useState('');
  const [startingRound, setStartingRound] = useState(false);
  const [courseSearch, setCourseSearch] = useState('');
  const [courseResults, setCourseResults] = useState([]);
  const [searchingCourses, setSearchingCourses] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [courseTeeBoxes, setCourseTeeBoxes] = useState([]);
  const [loadingCourseData, setLoadingCourseData] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [memberSearch, setMemberSearch] = useState('');
  const [memberResults, setMemberResults] = useState([]);
  const [searchingMembers, setSearchingMembers] = useState(false);
  const searchTimeout = useRef(null);
  const [inviteCourseName, setInviteCourseName] = useState('');
  const [inviteDateTime, setInviteDateTime] = useState('');
  const [sendingInvite, setSendingInvite] = useState(false);
  const [roundSetup, setRoundSetup] = useState({ tee_box: 'White', holes: 18, privacy: 'public', course_rating: '', slope_rating: '' });
  const [activityFeed, setActivityFeed] = useState([]);
  const [nearbyGolfers, setNearbyGolfers] = useState([]);
  const [showScanner, setShowScanner] = useState(false);
  const [scannerMode, setScannerMode] = useState('scan'); // 'scan' | 'manual'
  const [feedTab, setFeedTab] = useState('global');
  const [searchQuery, setSearchQuery] = useState('');
  const [liveStreams, setLiveStreams] = useState([]);
  const [golfMoments, setGolfMoments] = useState([]);
  const [welcomeVideo, setWelcomeVideo] = useState(null);
  const [recentRounds, setRecentRounds] = useState([]);
  const [bundleFeed, setBundleFeed] = useState([]);
  // Additional players for multi-player round
  const [additionalPlayers, setAdditionalPlayers] = useState([]);
  const [playerSearchQuery, setPlayerSearchQuery] = useState('');
  const [playerSearchResults, setPlayerSearchResults] = useState([]);

  const currentUser = location.state?.user || user;

  useEffect(() => { if (!loading && !currentUser) navigate('/'); }, [loading, currentUser, navigate]);
  useEffect(() => { if (currentUser) fetchData(); }, [currentUser]);

  // Deep link: /dashboard?scan=1 auto-opens the ScorecardScanner (used by PostRound CTA)
  useEffect(() => {
    if (!currentUser) return;
    const params = new URLSearchParams(location.search);
    if (params.get('scan') === '1') {
      setShowScanner(true);
      params.delete('scan');
      const search = params.toString();
      navigate(`/dashboard${search ? '?' + search : ''}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, location.search]);

  const fetchData = async () => {
    try {
      const [statsRes, storiesRes, eventsRes, lfpRes, promoRes, liveRes, myRoundRes, activityRes, nearbyRes, openRoundsRes] = await Promise.all([
        fetch(`${API}/stats`, { credentials: 'include' }),
        fetch(`${API}/stories?limit=3`, { credentials: 'include' }),
        fetch(`${API}/events?upcoming=true`, { credentials: 'include' }),
        fetch(`${API}/looking-for-players`, { credentials: 'include' }),
        fetch(`${API}/promo-videos`, { credentials: 'include' }),
        fetch(`${API}/live-rounds/feed`, { credentials: 'include' }),
        fetch(`${API}/live-rounds/active`, { credentials: 'include' }),
        fetch(`${API}/activity-feed`, { credentials: 'include' }),
        fetch(`${API}/nearby-golfers`, { credentials: 'include' }),
        fetch(`${API}/rounds/open`, { credentials: 'include' }),
      ]);
      if (statsRes.ok) setStats(await statsRes.json());
      if (storiesRes.ok) setRecentStories(await storiesRes.json());
      if (eventsRes.ok) { const events = await eventsRes.json(); setUpcomingEvents(events); }
      if (lfpRes.ok) setLookingForPlayers(await lfpRes.json());
      if (promoRes.ok) setPromoVideos(await promoRes.json());
      if (liveRes.ok) setLiveRoundFeed(await liveRes.json());
      if (myRoundRes.ok) setMyActiveRound(await myRoundRes.json());
      if (activityRes.ok) setActivityFeed(await activityRes.json());
      if (nearbyRes.ok) { const n = await nearbyRes.json(); setNearbyGolfers(Array.isArray(n) ? n.slice(0, 5) : []); }
      if (openRoundsRes.ok) { const r = await openRoundsRes.json(); setOpenRounds(Array.isArray(r) ? r : []); }
      // Fetch live streams and golf moments
      try {
        const lsRes = await fetch(`${API}/live-stream/active`, { credentials: 'include', headers: getAuthHeaders() });
        if (lsRes.ok) setLiveStreams(await lsRes.json());
      } catch {}
      try {
        const momRes = await fetch(`${API}/golf-moments/feed?limit=10`, { credentials: 'include', headers: getAuthHeaders() });
        if (momRes.ok) { const moms = await momRes.json(); setGolfMoments(moms); }
      } catch {}
      // Active welcome video (Sora 2 generated)
      try {
        const vRes = await fetch(`${API}/videos/active`, { credentials: 'include' });
        if (vRes.ok) {
          const v = await vRes.json();
          if (v && v.url) setWelcomeVideo(v);
        }
      } catch {}
      // Recent rounds (last 3 across all sources)
      try {
        const rrRes = await fetch(`${API}/me/rounds`, { credentials: 'include', headers: getAuthHeaders() });
        if (rrRes.ok) {
          const data = await rrRes.json();
          setRecentRounds((data.rounds || []).slice(0, 3));
        }
      } catch {}
      // Bundle Feed preview (latest 4 social posts — rounds, shared links, welcome)
      try {
        const fRes = await fetch(`${API}/social/feed?limit=4`, { credentials: 'include' });
        if (fRes.ok) {
          const data = await fRes.json();
          setBundleFeed(data.posts || []);
        }
      } catch (e) { console.error('feed fetch failed', e); }
    } catch (e) { console.error('Error fetching:', e); }
    finally { setLoadingData(false); }
  };

  const postLookingForPlayers = async () => {
    if (!lfpForm.course_name || !lfpForm.date || !lfpForm.time) { toast.error('Please fill in course, date, and time'); return; }
    setPostingLFP(true);
    try {
      const res = await fetch(`${API}/looking-for-players`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(lfpForm) });
      if (res.ok) { toast.success('Posted! Nearby members will be notified.'); setShowLFPModal(false); setLfpForm({ course_name: '', date: '', time: '', spots_needed: '1', message: '' }); fetchData(); }
      else toast.error('Failed to post');
    } catch { toast.error('Error posting'); } finally { setPostingLFP(false); }
  };

  const searchCourses = async (query) => {
    setCourseSearch(query);
    if (query.length < 2) { setCourseResults([]); return; }
    setSearchingCourses(true);
    try { const res = await fetch(`${API}/courses/search?query=${encodeURIComponent(query)}`, { credentials: 'include', headers: getAuthHeaders() }); if (res.ok) setCourseResults(await res.json()); }
    catch {} finally { setSearchingCourses(false); }
  };

  const selectCourse = async (course) => {
    setSelectedCourse(course); setCourseSearch(course.club_name || course.course_name); setCourseResults([]); setCourseTeeBoxes([]);
    if (course.id) {
      setLoadingCourseData(true);
      try {
        const res = await fetch(`${API}/course-scorecards/${course.id}`, { credentials: 'include', headers: getAuthHeaders() });
        if (res.ok) {
          const data = await res.json();
          const seen = new Set();
          const uniqueTees = (data.tee_boxes || []).filter(t => { if (seen.has(t.tee_name)) return false; seen.add(t.tee_name); return true; });
          setCourseTeeBoxes(uniqueTees);
          if (uniqueTees.length > 0) {
            const def = uniqueTees.find(t => t.tee_name === 'White') || uniqueTees[0];
            setRoundSetup(s => ({ ...s, tee_box: def.tee_name, course_rating: def.course_rating?.toString() || '', slope_rating: def.slope_rating?.toString() || '' }));
          }
        }
      } catch {} finally { setLoadingCourseData(false); }
    }
  };

  const linkEventToRound = (eventId) => {
    setLinkedEventId(eventId);
    if (!eventId) return;
    const ev = upcomingEvents.find(e => (e.event_id || e.id) === eventId);
    if (!ev) return;
    // Try to auto-populate the course from the event's attached course_id, then fall back to club_name/course name search
    const courseName = ev.club_name || ev.course_name || ev.location || '';
    if (ev.course_id) {
      selectCourse({ id: ev.course_id, club_name: courseName, course_name: courseName });
    } else if (courseName) {
      setSelectedCourse({ id: null, club_name: courseName, course_name: courseName });
      setCourseSearch(courseName);
      setCourseResults([]);
    }
  };

  const startLiveRound = async () => {
    const courseName = selectedCourse ? (selectedCourse.club_name || selectedCourse.course_name) : courseSearch;
    if (!courseName) { toast.error('Select or enter a course'); return; }
    setStartingRound(true);
    try {
      let pars = [], yardages = [], courseRating = roundSetup.course_rating ? parseFloat(roundSetup.course_rating) : null, slopeRating = roundSetup.slope_rating ? parseInt(roundSetup.slope_rating) : null;
      if (selectedCourse?.id) {
        try {
          const holeRes = await fetch(`${API}/course-scorecards/${selectedCourse.id}/holes?tee_name=${encodeURIComponent(roundSetup.tee_box)}`, { credentials: 'include', headers: getAuthHeaders() });
          if (holeRes.ok) { const d = await holeRes.json(); if (d.holes?.length) { pars = d.holes.slice(0, roundSetup.holes).map(h => h.par); yardages = d.holes.slice(0, roundSetup.holes).map(h => h.yardage || 0); if (!courseRating && d.course_rating) courseRating = d.course_rating; if (!slopeRating && d.slope_rating) slopeRating = d.slope_rating; } }
        } catch {}
      }
      const res = await fetch(`${API}/live-rounds/start`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ course_name: courseName, course_id: selectedCourse?.id || null, course_rating: courseRating, slope_rating: slopeRating, tee_box: roundSetup.tee_box, holes: roundSetup.holes, privacy: roundSetup.privacy, pars, yardages, additional_players: additionalPlayers.filter(p => p.name.trim()), linked_event_id: linkedEventId || null }) });
      if (res.ok) { const data = await res.json(); toast.success('Live round started!'); navigate(`/live-round/${data.live_round_id}`); }
      else { const err = await res.json().catch(() => ({})); toast.error(formatApiError(err.detail, 'Failed to start round')); }
    } catch { toast.error('Network error'); } finally { setStartingRound(false); }
  };

  const sendInvite = async () => {
    if (!inviteEmail) { toast.error('Enter an email address'); return; }
    if (!inviteCourseName) { toast.error('Enter a course name'); return; }
    if (!inviteDateTime) { toast.error('Enter a date & time'); return; }
    setSendingInvite(true);
    try {
      const res = await fetch(`${API}/email/round-invite`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ invited_email: inviteEmail, course_name: inviteCourseName, date_time: inviteDateTime }) });
      if (res.ok) { toast.success(`Invite sent to ${inviteEmail}!`); setShowInviteModal(false); setInviteEmail(''); setInviteCourseName(''); setInviteDateTime(''); setMemberSearch(''); setMemberResults([]); }
      else toast.error('Failed to send invite');
    } catch { toast.error('Network error'); } finally { setSendingInvite(false); }
  };

  const searchMembers = (query) => {
    setMemberSearch(query);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (query.length < 2) { setMemberResults([]); return; }
    setSearchingMembers(true);
    searchTimeout.current = setTimeout(async () => {
      try { const res = await fetch(`${API}/members/search?q=${encodeURIComponent(query)}`, { credentials: 'include' }); const data = await res.json(); setMemberResults(data.members || []); } catch { setMemberResults([]); }
      setSearchingMembers(false);
    }, 300);
  };

  const searchPlayersForRound = (query) => {
    setPlayerSearchQuery(query);
    if (query.length < 2) { setPlayerSearchResults([]); return; }
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(async () => {
      try { const res = await fetch(`${API}/members/search?q=${encodeURIComponent(query)}`, { credentials: 'include' }); const data = await res.json(); setPlayerSearchResults(data.members || []); } catch { setPlayerSearchResults([]); }
    }, 300);
  };

  const addPlayer = (name, userId) => {
    if (additionalPlayers.length >= 3) { toast.error('Maximum 3 additional players'); return; }
    if (additionalPlayers.some(p => p.name === name)) return;
    setAdditionalPlayers(prev => [...prev, { name, user_id: userId || null }]);
    setPlayerSearchQuery('');
    setPlayerSearchResults([]);
  };

  const removePlayer = (idx) => setAdditionalPlayers(prev => prev.filter((_, i) => i !== idx));

  const joinOpenRound = async (roundId) => {
    setJoiningRound(roundId);
    try {
      const res = await fetch(`${API}/rounds/open/${roundId}/join`, { method: 'POST', credentials: 'include' });
      if (res.ok) { toast.success("You're in! The organizer has been notified."); fetchData(); }
      else { const err = await res.json().catch(() => ({})); toast.error(formatApiError(err.detail, 'Failed to join')); }
    } catch { toast.error('Network error'); } finally { setJoiningRound(null); }
  };

  const buildRealCards = () => {
    const cards = [];
    liveRoundFeed.forEach(r => {
      const svp = r.score_vs_par;
      cards.push({ id: `live-${r.live_round_id}`, type: 'live', tab: ['live','my'], avatar: r.user_name?.[0] || '?', avatarColor: 'from-red-500 to-orange-500', name: r.user_name, time: formatTimeAgo(r.started_at), text: `Playing Hole ${r.current_hole}`, subtext: svp === 0 ? 'Even Par' : (svp > 0 ? `+${svp}` : `${svp}`), course: r.course_name, roundId: r.live_round_id, playerUserId: r.user_id, playerName: r.user_name });
    });
    openRounds.filter(r => r.status === 'open').forEach(r => {
      const spotsLeft = r.max_players - (r.current_players?.length || 0);
      cards.push({ id: `open-${r.round_id}`, type: 'open', tab: ['live','nearby'], spots: spotsLeft, total: r.max_players, current: r.current_players?.length || 0, time: `${r.date} at ${r.time}`, course: r.course_name, roundId: r.round_id, players: (r.players || []).map(p => p.name?.charAt(0) || '?') });
    });
    upcomingEvents.forEach(e => {
      cards.push({ id: `event-${e.event_id}`, type: 'upcoming', tab: ['live','nearby'], time: new Date(e.date).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' }), course: e.location || e.title, eventId: e.event_id });
    });
    return cards;
  };

  const feedCards = (() => {
    // Only real, actionable cards. If there are none, we render an empty state
    // below (no more mock/demo cards with broken links).
    const items = buildRealCards();
    if (feedTab === 'global') return items;
    if (feedTab === 'dmv') return items.filter(c => c.tab?.includes('live') || c.tab?.includes('nearby'));
    if (feedTab === 'mygroup') return items.filter(c => c.tab?.includes('my'));
    if (feedTab === 'results') return items.filter(c => c.tab?.includes('results'));
    return items;
  })();

  if (loading || !currentUser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bundle-cream">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-bundle-green"></div>
      </div>
    );
  }

  const firstName = currentUser.name?.split(' ')[0] || 'Golfer';

  // Find an open round the user can join (first one with open spots, not their own)
  const joinableRound = openRounds.find(r =>
    r.status === 'open' &&
    r.created_by !== currentUser.user_id &&
    !(r.current_players || []).some(p => p === currentUser.user_id || p?.user_id === currentUser.user_id) &&
    (r.max_players - (r.current_players?.length || 0)) > 0
  );
  const hasLiveRound = !!myActiveRound?.live_round_id;

  const upcomingCount = upcomingEvents.length;
  const liveCount = liveRoundFeed.length;

  const primaryAction = hasLiveRound
    ? { label: 'Resume Live Round', icon: Flag, onClick: () => navigate(`/live-round/${myActiveRound.live_round_id}`) }
    : joinableRound
    ? { label: 'Join Open Game', icon: Users, onClick: () => joinOpenRound(joinableRound.round_id) }
    : { label: 'Start Game', icon: Flag, onClick: () => setShowStartRound(true) };

  return (
    <div className="min-h-screen bg-bundle-cream pb-mobile-nav md:pb-8" data-testid="member-dashboard">
      <Navigation user={currentUser} onLogout={logout} />
      <NotificationPrompt />

      <div className="max-w-2xl mx-auto px-4 pt-[4.5rem] md:pt-20 pb-mobile-nav">

        {/* ===== WELCOME VIDEO (if admin has set one active) ===== */}
        {welcomeVideo?.url && (
          <div className="mb-5 rounded-2xl overflow-hidden bg-black shadow-xl relative group" data-testid="welcome-video">
            <video
              src={`${window.location.origin}${welcomeVideo.url}`}
              autoPlay
              muted
              loop
              playsInline
              className="w-full aspect-video object-cover pointer-events-none"
            />
            <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-bundle-gold/90 backdrop-blur-sm shadow-lg pointer-events-none">
              <Sparkles className="w-3 h-3 text-bundle-green-dark" />
              <span className="text-[10px] font-black uppercase tracking-wider text-bundle-green-dark">Welcome to Bundle</span>
            </div>
          </div>
        )}

        {/* ===== GREETING ===== */}
        <div className="mb-4 mt-2">
          <p className="text-[11px] uppercase tracking-widest text-bundle-green/60 font-semibold">{getGreeting()}</p>
          <h1 className="font-heading text-2xl font-bold text-bundle-green mt-0.5">Hey {firstName} 👋</h1>
        </div>

        {/* Complete-your-profile nudge — shown for admin-provisioned (Fundraiser / Director)
            accounts that skipped the signup wizard. Dismissable; navigates to /profile. */}
        {user?.needs_profile_completion && (
          <button
            type="button"
            onClick={() => navigate('/profile')}
            data-testid="complete-profile-banner"
            className="w-full mb-4 group relative overflow-hidden rounded-2xl bg-gradient-to-br from-bundle-gold to-bundle-gold-dark px-4 py-3 text-left shadow-lg active:scale-[0.99] transition-all"
          >
            <div className="relative flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-bundle-green-dark/15 flex items-center justify-center flex-shrink-0">
                <User className="w-5 h-5 text-bundle-green-dark" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-bundle-green-dark text-[10px] uppercase tracking-widest font-bold mb-0.5">Finish setting up</p>
                <p className="text-bundle-green-dark text-base font-black leading-tight">Complete your profile</p>
                <p className="text-bundle-green-dark/75 text-[11px] mt-0.5">Add your photo, golfer info & Bundle Tags so other members can find you</p>
              </div>
              <ChevronRight className="w-5 h-5 text-bundle-green-dark flex-shrink-0" />
            </div>
          </button>
        )}

        {/* ===== PRIMARY ACTION (TOP) ===== */}
        <button
          onClick={primaryAction.onClick}
          disabled={joiningRound === joinableRound?.round_id}
          data-testid="primary-action-btn"
          className="w-full mb-5 group relative overflow-hidden rounded-full bg-gradient-to-br from-bundle-green to-bundle-green-dark px-5 py-3 text-left shadow-xl shadow-bundle-green/20 active:scale-[0.99] transition-all"
        >
          <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-bundle-gold/10 blur-2xl group-hover:bg-bundle-gold/20 transition-colors" />
          <div className="absolute top-3 right-4 w-2 h-2 rounded-full bg-bundle-gold animate-pulse" />
          <div className="relative flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-bundle-gold flex items-center justify-center flex-shrink-0 shadow-lg">
              <primaryAction.icon className="w-6 h-6 text-bundle-green-dark" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-bundle-gold/80 text-[10px] uppercase tracking-widest font-bold mb-0.5">
                {hasLiveRound ? 'You have an active round' : joinableRound ? `${joinableRound.max_players - (joinableRound.current_players?.length || 0)} spots open right now` : 'Ready to play?'}
              </p>
              <p className="text-white text-xl font-black leading-tight">
                {joiningRound === joinableRound?.round_id ? 'Joining...' : primaryAction.label}
              </p>
              <p className="text-white/70 text-[11px] mt-0.5 truncate">
                {hasLiveRound ? `Hole ${myActiveRound.current_hole || '?'} · ${myActiveRound.course_name || ''}` : joinableRound ? `${joinableRound.course_name} · ${joinableRound.time}` : 'Tap to start a new live round'}
              </p>
            </div>
            <ChevronRight className="w-6 h-6 text-bundle-gold flex-shrink-0" />
          </div>
        </button>

        {/* Post a Tee Time — secondary primary CTA right under Start Game */}
        <button
          onClick={() => navigate('/create-event')}
          data-testid="post-tee-time-btn"
          className="w-full mb-5 group relative overflow-hidden rounded-full bg-gradient-to-br from-bundle-green to-bundle-green-dark px-5 py-3 text-left shadow-xl shadow-bundle-green/20 active:scale-[0.99] transition-all"
        >
          <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-bundle-gold/10 blur-2xl group-hover:bg-bundle-gold/20 transition-colors" />
          <div className="relative flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-bundle-gold flex items-center justify-center flex-shrink-0 shadow-lg">
              <Calendar className="w-6 h-6 text-bundle-green-dark" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-bundle-gold/80 text-[10px] uppercase tracking-widest font-bold mb-0.5">Organize a round</p>
              <p className="text-white text-xl font-black leading-tight">Post a Tee Time</p>
              <p className="text-white/70 text-[11px] mt-0.5 truncate">Invite fellow Bundle members to join</p>
            </div>
            <ChevronRight className="w-6 h-6 text-bundle-gold flex-shrink-0" />
          </div>
        </button>

        {/* Secondary actions below primary CTA */}
        <div className="grid grid-cols-5 gap-1.5 mb-6">
          <SecondaryQuickAction icon={Users} label="Players Near Me" onClick={() => setShowLFPModal(true)} testid="qa-find-players" />
          <SecondaryQuickAction icon={Camera} label="Scan Scorecard" onClick={() => { setScannerMode('scan'); setShowScanner(true); }} testid="qa-scan-card" />
          <SecondaryQuickAction icon={ClipboardEdit} label="Post Scorecard" onClick={() => { setScannerMode('manual'); setShowScanner(true); }} testid="qa-post-scorecard" />
          <SecondaryQuickAction icon={Share2} label="Social Post" onClick={() => navigate('/feed?share=1')} testid="qa-share-content" />
          <SecondaryQuickAction icon={Trophy} label="Leaderboard" onClick={() => navigate('/leaderboard')} testid="qa-leaderboard" />
          <SecondaryQuickAction icon={UserPlus} label="Invite" onClick={() => setShowInviteModal(true)} testid="qa-invite" />
        </div>

        {/* Bundle Tags™ — introduces the networking engine to every member */}
        <div className="mb-6">
          <BundleTagsInfoCard onManageTags={() => navigate('/profile')} />
        </div>

        {/* ===== TRENDING THIS WEEK — top 3 most-engaged posts (last 7 days) ===== */}
        <TrendingThisWeek onOpenPost={(postId) => navigate(`/feed?post=${postId}`)} />

        {/* ===== FACEBOOK-STYLE CLUB FEED COMPOSER ===== */}
        <ClubFeedComposer user={currentUser} onNavigate={navigate} />

        {/* ===== INLINE FEED PREVIEW — 3 most recent posts w/ Like + Comment ===== */}
        <DashboardFeedPreview
          user={currentUser}
          onOpenPost={(postId) => navigate(`/feed?post=${postId}`)}
          onSeeAll={() => navigate('/feed')}
        />

        {/* ===== EXPLORE THE CLUB ===== */}
        {/* Every key destination surfaced as a tappable tile so new members never have to hunt. */}
        <div className="mb-6" data-testid="explore-tiles">
          <div className="flex items-center justify-between mb-2">
            <h2 className="font-heading font-bold text-base text-bundle-green">Explore the Club</h2>
            <span className="text-[10px] text-gray-400">Tap any tile</span>
          </div>

          {/* Highlight: Trusted Golf Connections (search & connect with other members) */}
          <button
            type="button"
            onClick={() => navigate('/network')}
            data-testid="explore-trusted-connections"
            className="w-full mb-2.5 group relative overflow-hidden rounded-2xl bg-gradient-to-br from-bundle-gold/95 to-bundle-gold-dark/90 p-4 text-left shadow-lg active:scale-[0.99] transition-all border border-bundle-gold-dark/30"
          >
            <div className="absolute top-2 right-3">
              <span className="text-[9px] font-black uppercase tracking-widest bg-white/90 text-bundle-green-dark px-2 py-0.5 rounded-full animate-pulse">New</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-white/95 flex items-center justify-center flex-shrink-0 shadow-md">
                <Briefcase className="w-6 h-6 text-bundle-green-dark" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-bundle-green-dark/80 text-[10px] uppercase tracking-widest font-black mb-0.5">Trusted Golf Connections</p>
                <p className="text-bundle-green-dark text-base font-black leading-tight">Search Members' Businesses</p>
                <p className="text-bundle-green-dark/70 text-[11px] mt-0.5">Real estate, finance, legal, trades & more — refer fellow Bundle golfers.</p>
              </div>
              <Search className="w-5 h-5 text-bundle-green-dark/70 flex-shrink-0" />
            </div>
          </button>

          {/* Grid of remaining destinations */}
          <div className="grid grid-cols-3 gap-2">
            <ExploreTile icon={Calendar} label="Events" subtitle="Tournaments & meetups" onClick={() => navigate('/events')} testid="explore-events" tone="green" />
            <ExploreTile icon={Plane} label="Travel" subtitle="Group golf trips" onClick={() => navigate('/golf-trips')} testid="explore-travel" tone="blue" />
            <ExploreTile icon={Tag} label="Marketplace" subtitle="Gear buy/sell" onClick={() => navigate('/gear')} testid="explore-marketplace" tone="amber" />
            <ExploreTile icon={Tv} label="Bundle TV" subtitle="Club video reels" onClick={() => navigate('/bundle-tv')} testid="explore-bundle-tv" tone="red" />
            <ExploreTile icon={Users} label="Members" subtitle="Directory & golfers" onClick={() => navigate('/members')} testid="explore-members" tone="purple" />
            <ExploreTile icon={Flag} label="Book Tee Times" subtitle="Find courses & book" onClick={() => navigate('/book-tee-times')} testid="explore-book-tee-times" tone="blue" />
          </div>
        </div>

        {/* ===== BUNDLE NEWS HUB (top of feed) ===== */}
        <NewsHub />

        {/* ===== STACKED EXPANDABLE CARDS ===== */}
        <div className="space-y-3 mb-6" data-testid="stacked-cards">
          <ExpandableCard
            id="upcoming"
            icon={Calendar}
            iconColor="text-blue-600"
            iconBg="bg-blue-50"
            title="Upcoming Games"
            subtitle={upcomingCount > 0 ? `${upcomingCount} ${upcomingCount === 1 ? 'event' : 'events'} on the calendar` : 'No upcoming events'}
            badge={upcomingCount > 0 ? upcomingCount : null}
            onSeeAll={() => navigate('/events')}
          >
            {upcomingEvents.length === 0 ? (
              <p className="text-xs text-gray-400 py-2">Nothing scheduled yet. Check the events page or start your own.</p>
            ) : (
              <div className="space-y-2">
                {upcomingEvents.slice(0, 3).map(e => (
                  <button key={e.event_id} onClick={() => navigate(`/events?event=${e.event_id}`)}
                    data-testid={`upcoming-${e.event_id}`}
                    className="w-full flex items-center gap-3 p-2.5 rounded-xl bg-bundle-cream/50 hover:bg-bundle-cream active:scale-[0.98] transition-all text-left">
                    <div className="w-12 h-12 rounded-lg bg-white border border-bundle-cream-dark flex flex-col items-center justify-center flex-shrink-0">
                      <span className="text-[9px] font-bold text-bundle-gold-dark uppercase">{new Date(e.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short' })}</span>
                      <span className="text-base font-black text-bundle-green leading-none">{new Date(e.date + 'T00:00:00').getDate()}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-bundle-green truncate">{e.title}</p>
                      <p className="text-[11px] text-gray-500 truncate">{e.time} · {e.location}</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0" />
                  </button>
                ))}
              </div>
            )}
          </ExpandableCard>

          <ExpandableCard
            id="live"
            icon={Flame}
            iconColor="text-red-500"
            iconBg="bg-red-50"
            title="Live Game"
            subtitle={liveCount > 0 ? `${liveCount} ${liveCount === 1 ? 'round' : 'rounds'} in progress` : 'Nothing live right now'}
            badge={liveCount > 0 ? <span className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-red-50 text-red-500 text-[9px] font-bold border border-red-100"><span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />LIVE</span> : null}
            defaultOpen={liveCount > 0}
            onSeeAll={() => navigate('/leaderboard')}
          >
            {liveRoundFeed.length === 0 ? (
              <p className="text-xs text-gray-400 py-2">No live rounds. Tap "Start Game" above to begin one.</p>
            ) : (
              <div className="space-y-2">
                {liveRoundFeed.slice(0, 3).map(r => {
                  const svp = r.score_vs_par;
                  return (
                    <button key={r.live_round_id} onClick={() => navigate(`/live-round/${r.live_round_id}`)}
                      data-testid={`live-${r.live_round_id}`}
                      className="w-full flex items-center gap-3 p-2.5 rounded-xl bg-bundle-cream/50 hover:bg-bundle-cream active:scale-[0.98] transition-all text-left">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                        {r.user_name?.[0] || '?'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-bundle-green truncate">{r.user_name}</p>
                        <p className="text-[11px] text-gray-500 truncate">Hole {r.current_hole} · {r.course_name}</p>
                      </div>
                      <span className={`text-sm font-black ${svp < 0 ? 'text-red-500' : svp > 0 ? 'text-gray-700' : 'text-gray-700'}`}>
                        {svp === 0 ? 'E' : svp > 0 ? `+${svp}` : svp}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </ExpandableCard>

          <ExpandableCard
            id="bundle-feed"
            icon={Rss}
            iconColor="text-bundle-green"
            iconBg="bg-bundle-green/10"
            title="Bundle Feed"
            subtitle={bundleFeed.length > 0 ? 'Latest from the community' : 'No posts yet — share a link or post a round'}
            badge={bundleFeed.length > 0 ? bundleFeed.length : null}
            defaultOpen={bundleFeed.length > 0}
            onSeeAll={() => navigate('/feed')}
          >
            {bundleFeed.length === 0 ? (
              <div className="py-2">
                <p className="text-xs text-gray-400 mb-2">No posts yet — be the first.</p>
                <div className="flex gap-2">
                  <button onClick={() => navigate('/feed?share=1')} data-testid="feed-card-share-cta" className="flex-1 px-3 py-2 bg-bundle-green text-white text-xs font-bold rounded-lg flex items-center justify-center gap-1.5">
                    <Share2 className="w-3.5 h-3.5" /> Post a Link
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-3 -mx-1">
                {bundleFeed.slice(0, 2).map((p) => (
                  <FeedPreviewCard key={p.post_id} post={p} onOpen={() => navigate(`/feed?post=${p.post_id}`)} onNavigate={navigate} />
                ))}
                <div className="flex gap-2">
                  <button
                    onClick={() => navigate('/feed')}
                    data-testid="feed-see-all-link"
                    className="flex-1 px-3 py-2 bg-bundle-green text-white text-xs font-bold rounded-lg flex items-center justify-center gap-1.5 hover:bg-bundle-green-dark"
                  >
                    See all in Bundle Feed
                  </button>
                  <button
                    onClick={() => navigate('/feed?share=1')}
                    data-testid="feed-card-share-link"
                    className="px-3 py-2 border border-dashed border-bundle-green/40 text-bundle-green text-xs font-bold rounded-lg flex items-center justify-center gap-1.5 hover:bg-bundle-green/5"
                  >
                    <Share2 className="w-3.5 h-3.5" /> Post Link
                  </button>
                </div>
              </div>
            )}
          </ExpandableCard>

          <ExpandableCard
            id="recent-rounds"
            icon={Clock}
            iconColor="text-emerald-600"
            iconBg="bg-emerald-50"
            title="Recent Rounds"
            subtitle={recentRounds.length > 0 ? `${recentRounds.length} of your last rounds` : 'No rounds yet — tap Start Game to play'}
            badge={recentRounds.length > 0 ? recentRounds.length : null}
            defaultOpen={recentRounds.length > 0}
            onSeeAll={() => navigate('/rounds')}
          >
            {recentRounds.length === 0 ? (
              <p className="text-xs text-gray-400 py-2">Your rounds appear here once you complete or post one.</p>
            ) : (
              <div className="space-y-2">
                {recentRounds.map((r, idx) => {
                  const svp = r.score_vs_par;
                  const sourceLabels = { live_rounds: 'Live', social_posts: 'Posted', leaderboard_entries: 'Tagged', golf_rounds: 'Logged' };
                  const handleShare = async (e) => {
                    e.stopPropagation();
                    const text = `⛳ ${r.course_name} — Score ${r.score} (${svp === 0 ? 'E' : svp > 0 ? `+${svp}` : svp}) on ${r.date}\n${currentUser?.name ? `— ${currentUser.name}` : ''}`;
                    if (navigator.share) {
                      try { await navigator.share({ title: `Round at ${r.course_name}`, text }); return; } catch {}
                    }
                    try { await navigator.clipboard.writeText(text); toast.success('Round details copied'); }
                    catch { toast.error('Could not copy'); }
                  };
                  const handleClick = () => {
                    if (r.live_round_id) navigate(`/live-round/${r.live_round_id}`);
                    else navigate('/rounds');
                  };
                  return (
                    <div key={`${r.source}_${r.round_id || idx}`} data-testid={`recent-round-${idx}`}
                         className="flex items-center gap-3 p-2.5 rounded-xl bg-bundle-cream/50 hover:bg-bundle-cream transition-all">
                      <button onClick={handleClick} className="flex items-center gap-3 flex-1 min-w-0 text-left">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-bundle-green flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                          {r.score}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold text-bundle-green truncate">{r.course_name}</p>
                          <p className="text-[11px] text-gray-500 truncate">
                            <span className="font-semibold text-emerald-700">{sourceLabels[r.source] || r.source}</span> · {r.date}
                            <span className={`ml-1 font-semibold ${svp < 0 ? 'text-red-500' : svp > 0 ? 'text-gray-600' : 'text-bundle-green'}`}>
                              {svp === 0 ? 'E' : svp > 0 ? `+${svp}` : svp}
                            </span>
                          </p>
                        </div>
                      </button>
                      <button onClick={handleShare} data-testid={`recent-round-share-${idx}`}
                              className="p-2 rounded-full text-gray-400 hover:text-bundle-green hover:bg-bundle-green/10 transition-colors flex-shrink-0"
                              title="Share">
                        <Share2 className="w-4 h-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </ExpandableCard>

          <ExpandableCard
            id="dmv"
            icon={Trophy}
            iconColor="text-bundle-gold-dark"
            iconBg="bg-bundle-gold/10"
            title="DMV Leaderboard"
            subtitle={stats?.rank ? `You're ranked #${stats.rank} in DMV` : 'See where you stack up'}
            badge={stats?.rank ? <span className="px-1.5 py-0.5 rounded-md bg-bundle-gold/15 text-bundle-gold-dark text-[9px] font-black">#{stats.rank}</span> : null}
            onSeeAll={() => navigate('/leaderboard')}
          >
            <div className="space-y-2">
              <LeaderboardRow rank={1} name="Jamal R" score="-12" highlight={false} />
              <LeaderboardRow rank={2} name="Calvin P" score="-9" highlight={false} />
              <LeaderboardRow rank={3} name="Reggie K" score="-7" highlight={false} />
              {stats?.rank && stats.rank > 3 && (
                <>
                  <div className="text-center text-[10px] text-gray-300 py-0.5">···</div>
                  <LeaderboardRow rank={stats.rank} name={`${currentUser.name} (You)`} score={stats.handicap_index ? `${stats.handicap_index}` : '—'} highlight />
                </>
              )}
              <button onClick={() => navigate('/leaderboard')}
                data-testid="dmv-see-all"
                className="w-full mt-1 text-xs font-bold text-bundle-green hover:underline py-1">
                See full leaderboard →
              </button>
            </div>
          </ExpandableCard>

          <ExpandableCard
            id="stats"
            icon={TrendingUp}
            iconColor="text-bundle-green"
            iconBg="bg-bundle-green/10"
            title="My Stats"
            subtitle={stats?.handicap_index ? `Handicap ${stats.handicap_index}` : 'Track your progress'}
            badge={stats?.bundle_wins ? `${stats.bundle_wins} wins` : null}
            onSeeAll={() => navigate('/profile')}
          >
            <div className="grid grid-cols-2 gap-2">
              <StatTile label="Rank" value={stats?.rank ? `#${stats.rank}` : '—'} icon={Trophy} />
              <StatTile label="Bundle Wins" value={stats?.bundle_wins ?? 0} icon={Crown} accent />
              <StatTile label="Total Winnings" value={`$${(stats?.total_winnings || 0).toFixed(0)}`} icon={DollarSign} accent />
              <StatTile label="Handicap" value={stats?.handicap_index ?? '—'} icon={Target} />
              <StatTile label="Rounds Posted" value={stats?.rounds_played ?? 0} icon={Flag} />
              <StatTile label="Best Round" value={stats?.best_score ?? '—'} icon={TrendingUp} />
            </div>
          </ExpandableCard>
        </div>

        {/* ===== ACTIVITY FEED ===== */}
        <div className="mb-3 flex items-center justify-between" data-testid="activity-header">
          <h2 className="font-heading text-lg font-bold text-bundle-green flex items-center gap-2">
            <Rss className="w-4 h-4" /> Activity
          </h2>
          <button onClick={() => navigate('/feed')} className="text-xs font-bold text-bundle-green/70 hover:text-bundle-green">
            See all
          </button>
        </div>

        <div className="space-y-3" data-testid="activity-feed">
          {feedCards.length === 0 ? (
            <div className="bg-white border border-bundle-cream-dark rounded-2xl p-6 text-center" data-testid="feed-empty-state">
              <Calendar className="w-10 h-10 mx-auto text-bundle-cream-dark mb-2" />
              <p className="text-sm font-bold text-bundle-green mb-1">No activity yet</p>
              <p className="text-xs text-gray-500 mb-3">When members post open games, live rounds, or events, they&apos;ll appear here.</p>
              <button
                onClick={() => navigate('/events/create')}
                data-testid="feed-empty-post-tee-btn"
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-bundle-green text-white text-xs font-bold rounded-full active:scale-95 transition-transform"
              >
                <PlusCircle className="w-3.5 h-3.5" /> Post a Tee Time
              </button>
            </div>
          ) : (
            feedCards.slice(0, 8).map(card => (
              <FeedCard key={card.id} card={card} onJoinRound={joinOpenRound} joiningRound={joiningRound} navigate={navigate} />
            ))
          )}
        </div>
      </div>

      {/* ===== FAB ===== */}
      {!showScanner && <FloatingActionButton onScanCard={() => { setScannerMode('scan'); setShowScanner(true); }} onPostCard={() => { setScannerMode('manual'); setShowScanner(true); }} onStartRound={() => setShowStartRound(true)} navigate={navigate} />}

      {/* ===== MODALS ===== */}

      {/* Looking for Players */}
      <Dialog open={showLFPModal} onOpenChange={setShowLFPModal}>
        <DialogContent className="sm:max-w-md"><DialogHeader><DialogTitle className="text-bundle-green">Looking for Players</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div><Label className="text-sm text-gray-600">Golf Course *</Label><div className="mt-1"><CourseSearchInput value={lfpForm.course_name} onChange={val => setLfpForm({...lfpForm, course_name: val})} placeholder="Search golf courses..." /></div></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="text-sm text-gray-600">Date *</Label><Input data-testid="lfp-date-input" type="date" value={lfpForm.date} onChange={e => setLfpForm({...lfpForm, date: e.target.value})} className="mt-1" min={new Date().toISOString().split('T')[0]} /></div>
              <div><Label className="text-sm text-gray-600">Time *</Label><Input data-testid="lfp-time-input" type="time" value={lfpForm.time} onChange={e => setLfpForm({...lfpForm, time: e.target.value})} className="mt-1" /></div>
            </div>
            <div><Label className="text-sm text-gray-600">Players Needed</Label><select data-testid="lfp-spots-select" value={lfpForm.spots_needed} onChange={e => setLfpForm({...lfpForm, spots_needed: e.target.value})} className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-bundle-green"><option value="1">1</option><option value="2">2</option><option value="3">3</option></select></div>
            <div><Label className="text-sm text-gray-600">Message</Label><textarea data-testid="lfp-message-input" placeholder="Any details..." value={lfpForm.message} onChange={e => setLfpForm({...lfpForm, message: e.target.value})} className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-bundle-green min-h-[80px]" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowLFPModal(false)}>Cancel</Button><Button data-testid="lfp-submit-btn" onClick={postLookingForPlayers} disabled={postingLFP} className="btn-bundle">{postingLFP ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Posting...</> : <><Send className="w-4 h-4 mr-2" />Post</>}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Start Live Round — with multi-player support */}
      <Dialog open={showStartRound} onOpenChange={(open) => { setShowStartRound(open); if (!open) { setAdditionalPlayers([]); setPlayerSearchQuery(''); setPlayerSearchResults([]); setLinkedEventId(''); } }}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Flag className="w-5 h-5 text-red-500" /> Start Live Round</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            {/* Link to an existing event/tee time (optional) */}
            {upcomingEvents.length > 0 && (
              <div>
                <Label className="text-sm text-gray-600">Link to a Tournament or Tee Time (optional)</Label>
                <select
                  data-testid="link-event-select"
                  value={linkedEventId}
                  onChange={(e) => linkEventToRound(e.target.value)}
                  className="w-full mt-1 h-9 px-2 rounded-md border border-gray-200 text-sm bg-white"
                >
                  <option value="">— None / Casual round —</option>
                  {upcomingEvents.map(ev => {
                    const id = ev.event_id || ev.id;
                    const label = `${ev.title || ev.name || 'Event'} · ${ev.date || ''} ${ev.time || ''}`.trim();
                    return <option key={id} value={id}>{label}</option>;
                  })}
                </select>
                {linkedEventId && (
                  <p className="text-[10px] text-bundle-green mt-1">Course auto-filled from the event below.</p>
                )}
              </div>
            )}

            {/* Course Search */}
            <div>
              <Label className="text-sm text-gray-600">Golf Course *</Label>
              <div className="relative mt-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input data-testid="live-round-course-search" placeholder="Search courses..." value={courseSearch} onChange={e => { searchCourses(e.target.value); setSelectedCourse(null); setCourseTeeBoxes([]); }} className="pl-10" />
                {searchingCourses && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-gray-400" />}
                {courseResults.length > 0 && !selectedCourse && (
                  <div className="absolute z-10 w-full bg-white border border-gray-200 rounded-lg shadow-lg mt-1 max-h-40 overflow-y-auto">
                    {courseResults.slice(0, 5).map(c => (
                      <button key={c.id} className="w-full text-left px-3 py-2 hover:bg-bundle-cream text-sm border-b last:border-b-0" onClick={() => selectCourse(c)}>
                        <span className="font-medium text-bundle-green">{c.club_name || c.course_name}</span>
                        {c.location && <span className="text-gray-400 text-xs ml-2">{c.location.city}, {c.location.state}</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {selectedCourse && (
                <div className="mt-2 flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-lg" data-testid="course-selected-badge">
                  <MapPin className="w-4 h-4 text-green-600 flex-shrink-0" />
                  <div className="flex-1 min-w-0"><p className="text-sm font-semibold text-green-800 truncate">{selectedCourse.club_name || selectedCourse.course_name}</p></div>
                  {loadingCourseData && <Loader2 className="w-4 h-4 animate-spin text-green-500" />}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm text-gray-600">Tee Box</Label>
                <select data-testid="live-round-tee-box" value={roundSetup.tee_box} onChange={e => { const t = e.target.value; setRoundSetup(s => ({ ...s, tee_box: t })); const tee = courseTeeBoxes.find(x => x.tee_name === t); if (tee) setRoundSetup(s => ({ ...s, tee_box: t, course_rating: tee.course_rating?.toString() || s.course_rating, slope_rating: tee.slope_rating?.toString() || s.slope_rating })); }} className="w-full mt-1 h-9 px-2 rounded-md border border-gray-200 text-sm">
                  {courseTeeBoxes.length > 0 ? courseTeeBoxes.map(t => <option key={t.tee_name} value={t.tee_name}>{t.tee_name}</option>) : ['Black','Blue','White','Gold','Red'].map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-sm text-gray-600">Holes</Label>
                <div className="flex gap-2 mt-1">
                  {[9, 18].map(h => (
                    <button key={h} data-testid={`holes-btn-${h}`} onClick={() => setRoundSetup(s => ({ ...s, holes: h }))} className={`flex-1 h-9 rounded-md text-sm font-bold transition-all ${roundSetup.holes === h ? 'bg-bundle-green text-white' : 'bg-bundle-cream text-gray-600'}`}>{h}</button>
                  ))}
                </div>
              </div>
            </div>

            {/* Players Section */}
            <div>
              <Label className="text-sm text-gray-600 flex items-center gap-2">
                <Users className="w-4 h-4" /> Players ({1 + additionalPlayers.length}/4)
              </Label>
              {/* Current user */}
              <div className="mt-2 flex items-center gap-2 px-3 py-2 bg-bundle-green/5 border border-bundle-green/20 rounded-lg">
                <div className="w-7 h-7 rounded-full bg-bundle-green flex items-center justify-center text-white text-xs font-bold">{firstName[0]}</div>
                <span className="text-sm font-medium text-bundle-green">{currentUser.name}</span>
                <span className="ml-auto text-[10px] text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">Scorer</span>
              </div>
              {/* Additional players */}
              {additionalPlayers.map((p, i) => (
                <div key={i} className="mt-2 flex items-center gap-2 px-3 py-2 bg-white border border-bundle-cream-dark rounded-lg" data-testid={`player-${i}`}>
                  <div className="w-7 h-7 rounded-full bg-bundle-gold/20 flex items-center justify-center text-bundle-gold-dark text-xs font-bold">{p.name[0]}</div>
                  <span className="text-sm font-medium text-gray-700">{p.name}</span>
                  {p.user_id && <span className="text-[10px] text-bundle-green bg-bundle-green/10 px-1.5 py-0.5 rounded-full">Member</span>}
                  <button onClick={() => removePlayer(i)} className="ml-auto p-1 rounded-full hover:bg-red-50 text-gray-400 hover:text-red-500"><X className="w-3.5 h-3.5" /></button>
                </div>
              ))}
              {/* Add player input */}
              {additionalPlayers.length < 3 && (
                <div className="mt-2 relative">
                  <Input data-testid="add-player-input" placeholder="Search member or type guest name..." value={playerSearchQuery} onChange={e => searchPlayersForRound(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && playerSearchQuery.trim()) { addPlayer(playerSearchQuery.trim()); } }}
                    className="text-sm" />
                  {playerSearchResults.length > 0 && (
                    <div className="absolute z-10 w-full bg-white border border-gray-200 rounded-lg shadow-lg mt-1 max-h-32 overflow-y-auto">
                      {playerSearchResults.slice(0, 4).map(m => (
                        <button key={m.user_id} onClick={() => addPlayer(m.name, m.user_id)} className="w-full text-left px-3 py-2 hover:bg-bundle-cream text-sm flex items-center gap-2 border-b last:border-b-0">
                          <div className="w-6 h-6 rounded-full bg-bundle-green/10 flex items-center justify-center text-bundle-green text-xs font-bold">{m.name?.[0]}</div>
                          <span className="font-medium">{m.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  <p className="text-[10px] text-gray-400 mt-1">Press Enter to add as guest, or select a member</p>
                </div>
              )}
            </div>

            {/* Visibility */}
            <div>
              <Label className="text-sm text-gray-600">Visibility</Label>
              <div className="flex gap-2 mt-1">
                {[{ value: 'public', label: 'Public' }, { value: 'friends', label: 'Friends' }, { value: 'private', label: 'Private' }].map(opt => (
                  <button key={opt.value} data-testid={`privacy-btn-${opt.value}`} onClick={() => setRoundSetup(s => ({ ...s, privacy: opt.value }))} className={`flex-1 h-9 rounded-md text-xs font-medium transition-all ${roundSetup.privacy === opt.value ? 'bg-bundle-green text-white' : 'bg-bundle-cream text-gray-600'}`}>{opt.label}</button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStartRound(false)}>Cancel</Button>
            <Button data-testid="start-round-confirm-btn" onClick={startLiveRound} disabled={startingRound || (!selectedCourse && courseSearch.length < 2)} className="btn-bundle">
              {startingRound ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Flag className="w-4 h-4 mr-2" />} Start Round
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invite Golfer */}
      <Dialog open={showInviteModal} onOpenChange={setShowInviteModal}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><UserPlus className="w-5 h-5 text-blue-600" /> Invite a Golfer</DialogTitle><DialogDescription className="text-sm text-gray-500">Send an email invite to join you for a round.</DialogDescription></DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="relative">
              <Label className="text-sm text-gray-600">Search Member</Label>
              <Input data-testid="member-search-input" placeholder="Start typing a member's name..." value={memberSearch} onChange={e => searchMembers(e.target.value)} className="mt-1" />
              {searchingMembers && <Loader2 className="absolute right-3 top-9 w-4 h-4 animate-spin text-gray-400" />}
              {memberResults.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto" data-testid="member-search-results">
                  {memberResults.map(m => (
                    <button key={m.user_id} onClick={() => { setInviteEmail(m.email); setMemberSearch(m.name); setMemberResults([]); }} className="w-full text-left px-3 py-2.5 hover:bg-gray-50 flex items-center gap-3 border-b last:border-0" data-testid={`member-result-${m.user_id}`}>
                      {m.picture ? <img src={m.picture} alt="" className="w-8 h-8 rounded-full object-cover" /> : <div className="w-8 h-8 rounded-full bg-bundle-green/10 flex items-center justify-center text-bundle-green font-bold text-sm">{m.name?.charAt(0)}</div>}
                      <div className="flex-1 min-w-0"><p className="text-sm font-semibold truncate">{m.name}</p><p className="text-xs text-gray-400 truncate">{m.home_course || m.email}</p></div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="flex items-center gap-3"><div className="flex-1 border-t border-gray-200" /><span className="text-xs text-gray-400">or enter email</span><div className="flex-1 border-t border-gray-200" /></div>
            <div><Label className="text-sm text-gray-600">Their Email</Label><Input data-testid="invite-email-input" type="email" placeholder="golfer@email.com" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} className="mt-1" /></div>
            <div><Label className="text-sm text-gray-600">Course Name</Label><div className="mt-1"><CourseSearchInput value={inviteCourseName} onChange={val => setInviteCourseName(val)} placeholder="Search golf courses..." /></div></div>
            <div><Label className="text-sm text-gray-600">Date & Time</Label><Input data-testid="invite-datetime-input" placeholder="e.g. Saturday March 22, 10:00 AM" value={inviteDateTime} onChange={e => setInviteDateTime(e.target.value)} className="mt-1" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowInviteModal(false)}>Cancel</Button><Button data-testid="send-invite-btn" onClick={sendInvite} disabled={sendingInvite} className="bg-blue-600 hover:bg-blue-700 text-white">{sendingInvite ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />} Send Invite</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Scorecard Scanner — supports both 'scan' (AI) and 'manual' (hole-by-hole entry) modes */}
      {showScanner && (
        <ScorecardScanner
          startMode={scannerMode}
          currentUser={currentUser}
          onClose={() => { setShowScanner(false); setScannerMode('scan'); }}
          onScanComplete={(result) => {
          setShowScanner(false);
          setScannerMode('scan');
          if (result.action === 'post') navigate('/feed', { state: { scanData: result.data } });
          else if (result.action === 'tournament') navigate('/events', { state: { scanData: result.data } });
          else if (result.action === 'event') navigate('/events', { state: { scanData: result.data, createEvent: true } });
          else if (result.action === 'processor') navigate('/tournament-processor', { state: { scanData: result.data, skins: result.skins } });
          else toast.success('Round saved to history');
        }} />
      )}

      {/* Quick Scan FAB */}
      {!showScanner && (
        <button onClick={() => setShowScanner(true)} data-testid="quick-scan-fab"
          className="fixed z-40 w-14 h-14 bg-gradient-to-br from-purple-600 to-purple-800 rounded-full shadow-lg shadow-purple-500/30 flex items-center justify-center text-white hover:scale-105 active:scale-95 transition-transform md:bottom-6 md:right-6"
          style={{ bottom: 'calc(85px + env(safe-area-inset-bottom, 0px))', right: '16px' }}>
          <Camera className="w-6 h-6" />
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-bundle-gold rounded-full flex items-center justify-center"><span className="text-[10px] font-bold text-bundle-green-dark">AI</span></span>
        </button>
      )}
    </div>
  );
}

/* ===== LIVE GOLF MOMENTS CAROUSEL ===== */

const SAMPLE_MOMENTS = [
  { id: 'sm1', type: 'sample', name: 'Andre S', course: 'Lake Presidential', badge: 'LIVE', gradient: 'from-red-600 to-orange-500', desc: 'Tee shot clip' },
  { id: 'sm2', type: 'sample', name: 'Mike T', course: 'Renditions', badge: '5 min ago', gradient: 'from-bundle-green to-emerald-600', desc: 'Putt drops' },
  { id: 'sm3', type: 'sample', name: 'DMV Group', course: '', badge: 'HOT', gradient: 'from-amber-500 to-red-500', desc: 'Group reaction' },
  { id: 'sm4', type: 'sample', name: 'Chris W', course: 'Whiskey Creek', badge: '', gradient: 'from-blue-600 to-indigo-600', desc: 'Drive highlight' },
];

const GRADIENTS = ['from-red-600 to-orange-500', 'from-bundle-green to-emerald-600', 'from-amber-500 to-red-500', 'from-blue-600 to-indigo-600', 'from-purple-600 to-pink-500', 'from-teal-500 to-cyan-600'];

function LiveGolfMoments({ liveStreams, golfMoments, navigate }) {
  const scrollRef = React.useRef(null);
  const formatTimeAgo = (d) => {
    if (!d) return '';
    const diff = (Date.now() - new Date(d).getTime()) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  const allMoments = [
    // Real live streams first
    ...liveStreams.map(s => ({
      id: s.stream_id, type: 'live', name: s.user_name, course: s.course_name,
      badge: 'LIVE', viewers: s.viewer_count, gradient: 'from-red-600 to-orange-500',
      desc: s.title || 'Live Round', link: `/go-live/${s.stream_id}`,
    })),
    // Real golf moments (uploaded videos + YouTube)
    ...golfMoments.map((m, i) => ({
      id: m.moment_id, type: m.media_type, name: m.user_name, course: m.course_name,
      badge: formatTimeAgo(m.created_at), gradient: GRADIENTS[i % GRADIENTS.length],
      desc: m.title, link: `/go-live/${m.moment_id}`,
      mediaUrl: m.media_url, youtubeId: m.youtube_id,
      views: m.view_count, reactions: m.reactions,
    })),
    // Sample moments as fallback — tap navigates to Course Cam
    ...(liveStreams.length === 0 && golfMoments.length === 0 ? SAMPLE_MOMENTS.map(s => ({ ...s, link: '/go-live' })) : []),
  ];

  return (
    <div className="mb-5 -mx-4" data-testid="live-golf-moments">
      {/* Header */}
      <div className="px-4 mb-3">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
              <h2 className="font-heading font-bold text-base text-bundle-green">The Bundle Golf Club</h2>
              {liveStreams.length > 0 && (
                <span className="text-[10px] text-red-500 font-bold bg-red-50 px-1.5 py-0.5 rounded-full border border-red-100">{liveStreams.length} LIVE</span>
              )}
            </div>
            <p className="text-[11px] text-gray-400 mt-0.5 ml-4">See what's happening on the course right now</p>
          </div>
        </div>
      </div>

      {/* Carousel */}
      <div ref={scrollRef} className="flex gap-3 overflow-x-auto px-4 pb-2 scrollbar-hide snap-x snap-mandatory" style={{ scrollSnapType: 'x mandatory' }}>
        {allMoments.map((m, i) => (
          <button key={m.id}
            onClick={() => m.link ? navigate(m.link) : null}
            data-testid={`moment-card-${m.id}`}
            className="flex-shrink-0 w-[130px] snap-start group"
            style={{ animationDelay: `${i * 60}ms` }}
          >
            {/* Video Card */}
            <div className={`relative w-[130px] h-[180px] rounded-2xl overflow-hidden bg-gradient-to-br ${m.gradient} shadow-lg transition-transform duration-300 group-hover:scale-[1.03] group-active:scale-95`}>
              {/* YouTube thumbnail or video preview */}
              {m.youtubeId && (
                <img src={`https://img.youtube.com/vi/${m.youtubeId}/hqdefault.jpg`} alt="" className="absolute inset-0 w-full h-full object-cover" />
              )}
              {m.mediaUrl && m.type === 'upload' && (
                <video src={m.mediaUrl} className="absolute inset-0 w-full h-full object-cover" muted playsInline loop
                  onMouseEnter={e => e.target.play()} onMouseLeave={e => e.target.pause()} />
              )}
              {/* Dark overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/20 z-[1]" />

              {/* Center play icon */}
              <div className="absolute inset-0 flex items-center justify-center z-[2]">
                <div className="w-10 h-10 rounded-full bg-white/15 backdrop-blur-sm flex items-center justify-center border border-white/20">
                  <Video className="w-5 h-5 text-white/70" />
                </div>
              </div>

              {/* Top Badge */}
              <div className="absolute top-2 right-2 z-[3]">
                {m.badge === 'LIVE' ? (
                  <div className="flex items-center gap-1 px-2 py-0.5 bg-red-500 rounded-full shadow-lg shadow-red-500/30">
                    <div className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                    <span className="text-white text-[9px] font-bold">LIVE</span>
                  </div>
                ) : m.badge === 'HOT' ? (
                  <div className="flex items-center gap-1 px-2 py-0.5 bg-orange-500 rounded-full">
                    <Flame className="w-2.5 h-2.5 text-white" />
                    <span className="text-white text-[9px] font-bold">HOT</span>
                  </div>
                ) : m.badge ? (
                  <div className="px-2 py-0.5 bg-black/40 backdrop-blur-sm rounded-full">
                    <span className="text-white/80 text-[9px]">{m.badge}</span>
                  </div>
                ) : null}
              </div>

              {/* Viewer count for live */}
              {m.viewers > 0 && (
                <div className="absolute top-2 left-2 z-[3] flex items-center gap-1 px-1.5 py-0.5 bg-black/30 backdrop-blur-sm rounded-full">
                  <Eye className="w-2.5 h-2.5 text-white/70" />
                  <span className="text-white/70 text-[9px]">{m.viewers}</span>
                </div>
              )}

              {/* Bottom info */}
              <div className="absolute bottom-0 left-0 right-0 p-2.5 z-[3]">
                <p className="text-white font-bold text-xs leading-tight truncate">{m.name}</p>
                {m.course && (
                  <p className="text-white/60 text-[10px] flex items-center gap-0.5 mt-0.5 truncate">
                    <MapPin className="w-2.5 h-2.5 flex-shrink-0" /> {m.course}
                  </p>
                )}
                {m.desc && <p className="text-white/40 text-[9px] mt-0.5 truncate">{m.desc}</p>}
              </div>
            </div>
          </button>
        ))}

        {/* Course Cam Card — Always last */}
        <button onClick={() => navigate('/go-live')} data-testid="moment-course-cam-btn"
          className="flex-shrink-0 w-[130px] h-[180px] snap-start rounded-2xl border-2 border-dashed border-bundle-green/30 bg-bundle-green/5 flex flex-col items-center justify-center gap-2 hover:bg-bundle-green/10 hover:border-bundle-green/50 active:scale-95 transition-all">
          <div className="w-12 h-12 rounded-full bg-bundle-gold/10 flex items-center justify-center">
            <Video className="w-6 h-6 text-bundle-gold" />
          </div>
          <span className="text-xs font-bold text-bundle-green">Course Cam</span>
          <span className="text-[9px] text-gray-400">Upload or share</span>
        </button>
      </div>
    </div>
  );
}

/* ===== FEED CARD COMPONENTS ===== */

function FeedCard({ card, onJoinRound, joiningRound, navigate }) {
  const base = "rounded-2xl border p-4 transition-all duration-300 active:scale-[0.99] shadow-sm";

  if (card.type === 'live') {
    const isEagle = card.icon === 'eagle' || card.text?.includes('EAGLE');
    return (
      <div className={`${base} bg-white ${isEagle ? 'border-bundle-gold/30' : 'border-bundle-cream-dark'} hover:shadow-md`} data-testid={`feed-card-${card.id}`}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2.5">
            <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${card.avatarColor} flex items-center justify-center text-white text-xs font-bold`}>{card.avatar}</div>
            <div><p className="text-sm font-bold text-bundle-green">{card.name}</p><p className="text-[10px] text-gray-400">{card.time}</p></div>
          </div>
          <span className="px-2.5 py-0.5 bg-red-50 text-red-500 text-[10px] font-bold rounded-full flex items-center gap-1.5 border border-red-100">
            <div className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" /> LIVE
          </span>
        </div>
        <p className="text-sm text-gray-800 mb-1">
          {isEagle ? <><span className="text-bundle-gold font-black">EAGLE</span> <span className="font-bold">{card.text?.replace('EAGLE ', '')}</span></> : <><Flame className="w-4 h-4 text-orange-500 inline mr-1" /><span className="font-bold">{card.text}</span></>}
        </p>
        <p className="text-xs text-gray-400 flex items-center gap-1 mb-3"><MapPin className="w-3 h-3" /> {card.course}</p>
        {card.leaderboard && (
          <div className="space-y-1 mb-3 p-2.5 rounded-xl bg-bundle-cream">
            <p className="text-[10px] text-gray-500 font-bold uppercase mb-1">Top 3</p>
            {card.leaderboard.map((p, i) => (
              <div key={i} className="flex items-center justify-between text-xs">
                <span className="text-gray-700"><span className={`font-bold ${i === 0 ? 'text-bundle-gold' : i === 1 ? 'text-gray-500' : 'text-amber-600'}`}>{i+1}.</span> {p.name}</span>
                <span className={`font-bold ${p.score.startsWith('-') ? 'text-red-500' : 'text-gray-700'}`}>{p.score}</span>
              </div>
            ))}
          </div>
        )}
        <div className="flex items-center gap-2">
          {card.roundId && <button onClick={() => navigate(`/live-round/${card.roundId}`)} className="flex-1 py-2 bg-bundle-cream border border-bundle-cream-dark text-bundle-green font-bold text-xs rounded-xl hover:bg-bundle-cream-dark transition-colors">View Game</button>}
          {card.playerUserId && (
            <button
              onClick={() => navigate('/chat', { state: { startDmWith: { user_id: card.playerUserId, name: card.playerName }, prefillMessage: `Saw you're live at ${card.course} — good luck out there!` } })}
              data-testid={`feed-live-message-${card.roundId}`}
              className="flex-1 py-2 bg-bundle-green text-white font-bold text-xs rounded-xl hover:bg-bundle-green-dark transition-colors"
            >
              Message
            </button>
          )}
        </div>
        <InteractionBar />
      </div>
    );
  }

  if (card.type === 'open') {
    return (
      <div className={`${base} bg-white border-bundle-green/20 hover:shadow-md relative overflow-hidden`} data-testid={`feed-card-${card.id}`}>
        <div className="absolute top-0 right-0 w-24 h-24 bg-bundle-green/5 rounded-full -mr-8 -mt-8" />
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] font-bold text-bundle-green uppercase tracking-wider flex items-center gap-1"><Flag className="w-3 h-3" /> Open Game</span>
          <span className="text-[10px] text-bundle-gold-dark font-bold animate-pulse">{card.spots} spot{card.spots !== 1 ? 's' : ''} left</span>
        </div>
        <p className="text-sm font-bold text-bundle-green mb-0.5">{card.time}</p>
        <p className="text-xs text-gray-400 flex items-center gap-1 mb-3"><MapPin className="w-3 h-3" /> {card.course}</p>
        <div className="flex items-center gap-1.5 mb-3">
          <Users className="w-3.5 h-3.5 text-gray-400" />
          {(card.players || []).slice(0, 4).map((init, i) => (
            <div key={i} className="w-7 h-7 rounded-full bg-bundle-green/10 border border-bundle-green/20 flex items-center justify-center text-[9px] font-bold text-bundle-green">{init}</div>
          ))}
          <span className="text-[10px] text-gray-400 ml-1">{card.current || card.players?.length || 0}/{card.total} players</span>
        </div>
        <div className="flex gap-2">
          <button onClick={() => card.roundId && onJoinRound(card.roundId)} disabled={joiningRound === card.roundId}
            className="flex-1 py-2.5 bg-bundle-green text-white font-bold text-xs rounded-xl hover:bg-bundle-green-dark transition-colors disabled:opacity-50">
            {joiningRound === card.roundId ? 'Joining...' : 'Join Game'}
          </button>
          <button
            onClick={() => navigate('/nearby-golfers')}
            data-testid={`feed-open-details-${card.roundId}`}
            className="px-4 py-2.5 bg-bundle-cream border border-bundle-cream-dark text-gray-600 font-bold text-xs rounded-xl hover:bg-bundle-cream-dark transition-colors"
          >
            Details
          </button>
        </div>
      </div>
    );
  }

  if (card.type === 'results') {
    return (
      <div className={`${base} bg-white border-bundle-cream-dark hover:shadow-md`} data-testid={`feed-card-${card.id}`}>
        <div className="flex items-center justify-between mb-3">
          <span className="text-[10px] font-bold text-bundle-gold-dark uppercase tracking-wider flex items-center gap-1"><Trophy className="w-3 h-3 text-bundle-gold" /> Final Results</span>
          <span className="px-2 py-0.5 bg-bundle-gold/10 text-bundle-gold-dark text-[10px] font-bold rounded-full border border-bundle-gold/20">FINAL</span>
        </div>
        <p className="text-sm font-bold text-bundle-green mb-2">{card.title}</p>
        <div className="space-y-1.5 mb-2">
          {(card.leaderboard || []).map((p, i) => (
            <div key={i} className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-2"><span className={`font-bold ${p.color || (i === 0 ? 'text-bundle-gold' : 'text-gray-400')}`}>{p.rank}</span><span className="text-gray-700">{p.name}</span></div>
              <span className={`font-bold ${p.score.startsWith('-') ? 'text-red-500' : 'text-gray-600'}`}>{p.score}</span>
            </div>
          ))}
        </div>
        {card.skins && <p className="text-xs text-green-600 mb-3 flex items-center gap-1"><DollarSign className="w-3 h-3" /> {card.skins}</p>}
        <button className="text-xs text-bundle-green font-bold hover:underline">View Leaderboard</button>
        <InteractionBar />
      </div>
    );
  }

  if (card.type === 'skins') {
    if (card.payouts) {
      return (
        <div className={`${base} bg-white border-bundle-cream-dark hover:shadow-md`} data-testid={`feed-card-${card.id}`}>
          <div className="flex items-center justify-between mb-3">
            <span className="text-[10px] font-bold text-green-600 uppercase tracking-wider flex items-center gap-1"><DollarSign className="w-3 h-3" /> Skins Payout</span>
            <span className="text-[10px] text-gray-400">{card.time}</span>
          </div>
          <p className="text-sm font-bold text-bundle-green mb-2">{card.title}</p>
          <div className="space-y-1.5 mb-3">
            {card.payouts.map((p, i) => (
              <div key={i} className="flex items-center justify-between text-xs">
                <span className="text-gray-700">{p.name} <span className="text-gray-400">({p.skins} skins)</span></span>
                <span className="font-bold text-green-600">{p.payout}</span>
              </div>
            ))}
          </div>
          <InteractionBar />
        </div>
      );
    }
    return (
      <div className={`${base} bg-white border-bundle-cream-dark hover:shadow-md`} data-testid={`feed-card-${card.id}`}>
        <div className="flex items-center gap-2 mb-2">
          <div className="w-8 h-8 rounded-full bg-green-50 flex items-center justify-center"><DollarSign className="w-4 h-4 text-green-600" /></div>
          <span className="text-[10px] font-bold text-green-600 uppercase">Skins Update</span>
          <span className="text-[10px] text-gray-400 ml-auto">{card.time}</span>
        </div>
        <p className="text-sm font-medium text-gray-800">{card.text}</p>
        {card.subtext && <p className="text-xs text-gray-500 mt-1">{card.subtext}</p>}
        <InteractionBar />
      </div>
    );
  }

  if (card.type === 'group') {
    return (
      <div className={`${base} bg-white border-bundle-cream-dark hover:shadow-md`} data-testid={`feed-card-${card.id}`}>
        <div className="flex items-center gap-2.5 mb-3">
          <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${card.avatarColor || 'from-bundle-green to-bundle-green-light'} flex items-center justify-center text-white text-[10px] font-bold`}>{card.avatar || 'G'}</div>
          <div><p className="text-sm font-bold text-bundle-green">{card.name}</p><p className="text-[10px] text-gray-400">{card.time}</p></div>
        </div>
        <p className="text-sm text-gray-700 mb-3">{card.text}</p>
        {card.playerCount && (
          <div className="flex items-center gap-1.5 mb-3">
            {Array.from({ length: Math.min(card.playerCount, 4) }).map((_, i) => <div key={i} className="w-7 h-7 rounded-full bg-bundle-cream-dark border border-bundle-cream-dark" />)}
            <span className="text-[10px] text-gray-400 ml-1">{card.playerCount} players</span>
          </div>
        )}
        <InteractionBar />
      </div>
    );
  }

  if (card.type === 'upcoming') {
    return (
      <div className={`${base} bg-white border-bundle-green/15 hover:shadow-md`} data-testid={`feed-card-${card.id}`}>
        <div className="flex items-center gap-2 mb-2">
          <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center"><Calendar className="w-4 h-4 text-blue-600" /></div>
          <span className="text-[10px] font-bold text-blue-600 uppercase">New Game Posted</span>
        </div>
        <p className="text-sm font-bold text-bundle-green mb-0.5">{card.time}</p>
        <p className="text-xs text-gray-400 flex items-center gap-1 mb-3"><MapPin className="w-3 h-3" /> {card.course}</p>
        <button
          onClick={() => card.eventId ? navigate(`/events?event=${card.eventId}`) : navigate('/events')}
          data-testid={`feed-upcoming-join-${card.eventId || card.id}`}
          className="w-full py-2.5 bg-bundle-green text-white font-bold text-xs rounded-xl hover:bg-bundle-green-dark transition-colors"
        >
          {card.eventId ? 'View & Join' : 'See on Calendar'}
        </button>
      </div>
    );
  }

  return null;
}

function InteractionBar() {
  return (
    <div className="flex items-center gap-3 text-gray-400 border-t border-bundle-cream-dark pt-2.5 mt-3">
      <button className="flex items-center gap-1 text-xs hover:text-orange-500 transition-colors active:scale-110"><Flame className="w-4 h-4" /> <span className="text-[10px]">12</span></button>
      <button className="flex items-center gap-1 text-xs hover:text-green-600 transition-colors active:scale-110"><DollarSign className="w-4 h-4" /> <span className="text-[10px]">5</span></button>
      <button className="flex items-center gap-1 text-xs hover:text-blue-500 transition-colors active:scale-110"><Eye className="w-4 h-4" /> <span className="text-[10px]">24</span></button>
    </div>
  );
}

function FloatingActionButton({ onScanCard, onPostCard, onStartRound, navigate }) {
  const [open, setOpen] = React.useState(false);
  return (
    <>
      {/* Overlay */}
      {open && <div className="fixed inset-0 bg-black/30 z-40" onClick={() => setOpen(false)} />}
      {/* FAB Menu */}
      <div className="fixed z-50" style={{ bottom: 'calc(85px + env(safe-area-inset-bottom, 0px))', right: '16px' }}>
        {open && (
          <div className="absolute bottom-16 right-0 flex flex-col gap-2 items-end mb-2" data-testid="fab-menu">
            <button onClick={() => { onScanCard(); setOpen(false); }} data-testid="fab-scan-scorecard" className="flex items-center gap-2 px-4 py-2.5 bg-white rounded-xl shadow-lg border border-bundle-cream-dark text-sm font-bold text-purple-600 active:scale-95 transition-transform">
              <Camera className="w-4 h-4" /> Scan Scorecard
            </button>
            <button onClick={() => { onPostCard(); setOpen(false); }} data-testid="fab-post-scorecard" className="flex items-center gap-2 px-4 py-2.5 bg-white rounded-xl shadow-lg border border-bundle-cream-dark text-sm font-bold text-emerald-600 active:scale-95 transition-transform">
              <ClipboardEdit className="w-4 h-4" /> Post Scorecard
            </button>
            <button onClick={() => { onStartRound(); setOpen(false); }} className="flex items-center gap-2 px-4 py-2.5 bg-white rounded-xl shadow-lg border border-bundle-cream-dark text-sm font-bold text-bundle-green active:scale-95 transition-transform">
              <Flag className="w-4 h-4" /> Start Live Round
            </button>
            <button onClick={() => { navigate('/go-live'); setOpen(false); }} className="flex items-center gap-2 px-4 py-2.5 bg-white rounded-xl shadow-lg border border-bundle-cream-dark text-sm font-bold text-bundle-gold-dark active:scale-95 transition-transform">
              <Video className="w-4 h-4" /> Course Cam
            </button>
            <button onClick={() => { navigate('/events/create'); setOpen(false); }} className="flex items-center gap-2 px-4 py-2.5 bg-white rounded-xl shadow-lg border border-bundle-cream-dark text-sm font-bold text-blue-600 active:scale-95 transition-transform">
              <Calendar className="w-4 h-4" /> Create Event
            </button>
          </div>
        )}
        {/* Main FAB */}
        <button onClick={() => setOpen(!open)} data-testid="fab-button"
          className={`w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all active:scale-90 ${open ? 'bg-gray-800 rotate-45' : 'bg-bundle-green shadow-bundle-green/30'}`}>
          <PlusCircle className="w-7 h-7 text-white" />
        </button>
      </div>
    </>
  );
}


/* ===== NEW DASHBOARD SUB-COMPONENTS ===== */

function SecondaryQuickAction({ icon: Icon, label, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="flex flex-col items-center justify-start gap-1.5 p-2 sm:p-3 rounded-full bg-white border border-bundle-cream-dark hover:border-bundle-green hover:shadow-md active:scale-95 transition-all min-h-[88px]"
    >
      <div className="w-10 h-10 rounded-full bg-bundle-cream flex items-center justify-center flex-shrink-0 shadow-sm">
        <Icon className="w-4 h-4 text-bundle-green" />
      </div>
      <span className="text-[10px] sm:text-[11px] font-bold text-bundle-green text-center leading-tight">{label}</span>
    </button>
  );
}

// Large, self-describing tile used in the "Explore the Club" grid. Each tile has its own
// accent color so destinations are visually distinct and discoverable at a glance — no
// hunting through "More" menus required.
const TILE_TONES = {
  green:  { bg: 'bg-bundle-green/10',  iconBg: 'bg-bundle-green',  iconText: 'text-white', border: 'border-bundle-green/20' },
  blue:   { bg: 'bg-blue-50',          iconBg: 'bg-blue-500',      iconText: 'text-white', border: 'border-blue-200' },
  amber:  { bg: 'bg-amber-50',         iconBg: 'bg-amber-500',     iconText: 'text-white', border: 'border-amber-200' },
  red:    { bg: 'bg-red-50',           iconBg: 'bg-red-500',       iconText: 'text-white', border: 'border-red-200' },
  purple: { bg: 'bg-purple-50',        iconBg: 'bg-purple-500',    iconText: 'text-white', border: 'border-purple-200' },
};
function ExploreTile({ icon: Icon, label, subtitle, onClick, testid, tone = 'green' }) {
  const t = TILE_TONES[tone] || TILE_TONES.green;
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`flex flex-col items-start gap-1.5 p-3 rounded-2xl border ${t.bg} ${t.border} hover:shadow-md active:scale-95 transition-all text-left min-h-[100px]`}
    >
      <div className={`w-9 h-9 rounded-xl ${t.iconBg} flex items-center justify-center shadow-sm`}>
        <Icon className={`w-4 h-4 ${t.iconText}`} />
      </div>
      <span className="text-xs font-black text-bundle-green leading-tight">{label}</span>
      <span className="text-[10px] text-gray-500 leading-tight">{subtitle}</span>
    </button>
  );
}


function FeedPreviewCard({ post, onOpen }) {
  const isShared = post.post_type === 'shared_content';
  const isWelcome = post.post_type === 'welcome';
  const platformLabel = post.platform === 'youtube' ? 'YouTube' : post.platform === 'tiktok' ? 'TikTok' : post.platform === 'instagram' ? 'Instagram' : 'Link';
  const platformColor = post.platform === 'youtube' ? 'bg-red-500/10 text-red-600 border-red-200' : post.platform === 'tiktok' ? 'bg-gray-900/10 text-gray-900 border-gray-300' : post.platform === 'instagram' ? 'bg-pink-500/10 text-pink-600 border-pink-200' : 'bg-gray-100 text-gray-600 border-gray-200';

  const timeAgo = (() => {
    if (!post.created_at) return '';
    const d = (Date.now() - new Date(post.created_at).getTime()) / 1000;
    if (d < 60) return 'Just now';
    if (d < 3600) return `${Math.floor(d / 60)}m`;
    if (d < 86400) return `${Math.floor(d / 3600)}h`;
    return `${Math.floor(d / 86400)}d`;
  })();

  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid={`feed-preview-${post.post_id}`}
      className="w-full text-left bg-white rounded-2xl border border-bundle-cream-dark overflow-hidden hover:shadow-md transition-shadow group block"
    >
      <div className="flex items-center gap-2.5 px-3 pt-3 pb-2">
        <div className="w-9 h-9 rounded-full bg-bundle-green/10 flex items-center justify-center overflow-hidden flex-shrink-0">
          {post.user_picture ? (
            <img src={post.user_picture} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
          ) : (
            <span className="text-xs font-bold text-bundle-green">{post.user_name?.[0]?.toUpperCase() || '?'}</span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-bundle-green truncate leading-tight">{post.user_name}</p>
          <p className="text-[10px] text-gray-400 leading-tight">
            {isShared ? `shared a ${platformLabel.toLowerCase()}` : isWelcome ? 'joined Bundle' : post.is_observer_post ? `posted a group round at ${post.course_name}` : post.score != null ? `posted a round at ${post.course_name}` : 'posted'}
            {' · '}{timeAgo}
          </p>
        </div>
      </div>

      {post.caption && (
        <p className="px-3 pb-2 text-[13px] text-gray-700 line-clamp-2">{post.caption}</p>
      )}

      {isShared && post.thumbnail_url ? (
        <div className="relative w-full aspect-video bg-gray-100">
          <img src={post.thumbnail_url} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
          {post.platform === 'youtube' && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-12 h-12 bg-red-600/90 group-hover:bg-red-600 rounded-full flex items-center justify-center shadow-lg transition-transform group-hover:scale-110">
                <Play className="w-5 h-5 text-white fill-current ml-0.5" />
              </div>
            </div>
          )}
          <span className={`absolute top-2 left-2 text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${platformColor}`}>{platformLabel}</span>
        </div>
      ) : isShared ? (
        <div className="px-3 py-3 bg-gray-50 border-y border-gray-200 flex items-center gap-2 text-xs text-gray-600">
          <ExternalLink className="w-4 h-4 flex-shrink-0" /> <span className="truncate">{post.shared_url}</span>
        </div>
      ) : isWelcome ? (
        <div className="px-3 pb-3">
          <div className="w-full py-4 rounded-xl bg-gradient-to-br from-emerald-500 to-bundle-green text-white text-center">
            <UserPlus className="w-6 h-6 mx-auto mb-1" />
            <p className="font-bold text-sm">Welcome {post.user_name?.split(' ')[0]}!</p>
            <p className="text-[11px] opacity-90">New Bundle member</p>
          </div>
        </div>
      ) : (
        <div className="px-3 pb-3">
          {post.is_observer_post ? (
            (() => {
              const players = post.scorecard_data?.players || [];
              let low = null;
              players.forEach(p => {
                const t = parseInt(p?.scores?.total, 10);
                if (!Number.isNaN(t) && t > 0 && (low === null || t < low.total)) {
                  low = { name: p.name || 'Player', total: t };
                }
              });
              return (
                <div className="w-full py-4 rounded-xl bg-gradient-to-br from-blue-500 to-blue-700 text-white text-center">
                  <p className="text-[10px] uppercase tracking-wider opacity-80 mb-1">Group Round · {post.course_name}</p>
                  {low ? (
                    <>
                      <p className="text-3xl font-black leading-none">{low.total}</p>
                      <p className="text-[11px] mt-1 opacity-90">low by {low.name}</p>
                    </>
                  ) : (
                    <p className="text-[12px] mt-1 opacity-90">{players.length} players</p>
                  )}
                </div>
              );
            })()
          ) : (
            <div className="w-full py-5 rounded-xl bg-gradient-to-br from-emerald-500 to-bundle-green text-white text-center">
              <p className="text-[10px] uppercase tracking-wider opacity-80 mb-1">{post.course_name}</p>
              <p className="text-4xl font-black leading-none">{post.score ?? '—'}</p>
              {post.score_vs_par != null && (
                <p className="text-[11px] mt-1 opacity-90">{post.score_vs_par > 0 ? `+${post.score_vs_par}` : post.score_vs_par === 0 ? 'E' : post.score_vs_par}</p>
              )}
            </div>
          )}
        </div>
      )}

      {(post.likes?.length > 0 || post.comment_count > 0) && (
        <div className="flex items-center gap-3 px-3 py-2 border-t border-bundle-cream-dark text-[11px] text-gray-500">
          {post.likes?.length > 0 && <span className="flex items-center gap-1"><Heart className="w-3 h-3 text-red-500 fill-current" />{post.likes.length}</span>}
          {post.comment_count > 0 && <span className="flex items-center gap-1"><MessageCircle className="w-3 h-3" />{post.comment_count}</span>}
          <span className="ml-auto text-bundle-green font-bold flex items-center gap-0.5">View <ChevronRight className="w-3 h-3" /></span>
        </div>
      )}
    </button>
  );
}

function ExpandableCard({ id, icon: Icon, iconColor, iconBg, title, subtitle, badge, defaultOpen = false, onSeeAll, children }) {
  const [open, setOpen] = React.useState(defaultOpen);
  const prevDefault = React.useRef(defaultOpen);
  // Sync if defaultOpen changes after first render (e.g. data loaded async)
  React.useEffect(() => {
    if (defaultOpen && !prevDefault.current) setOpen(true);
    prevDefault.current = defaultOpen;
  }, [defaultOpen]);
  return (
    <div className="bg-white rounded-2xl border border-bundle-cream-dark overflow-hidden" data-testid={`stack-card-${id}`}>
      <button
        onClick={() => setOpen(o => !o)}
        data-testid={`stack-card-${id}-toggle`}
        className="w-full flex items-center gap-3 p-4 hover:bg-bundle-cream/30 active:bg-bundle-cream/50 transition-colors text-left"
      >
        <div className={`w-10 h-10 rounded-xl ${iconBg} flex items-center justify-center flex-shrink-0`}>
          <Icon className={`w-5 h-5 ${iconColor}`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-heading font-bold text-bundle-green text-base leading-tight">{title}</p>
          <p className="text-[11px] text-gray-500 truncate">{subtitle}</p>
        </div>
        {badge !== null && badge !== undefined && (
          <div className="flex-shrink-0">
            {typeof badge === 'number' ? (
              <span className="px-2 py-0.5 rounded-full bg-bundle-green/10 text-bundle-green text-[11px] font-bold">{badge}</span>
            ) : typeof badge === 'string' ? (
              <span className="px-2 py-0.5 rounded-full bg-bundle-gold/15 text-bundle-gold-dark text-[10px] font-bold">{badge}</span>
            ) : (
              badge
            )}
          </div>
        )}
        <ChevronRight className={`w-4 h-4 text-gray-300 transition-transform flex-shrink-0 ${open ? 'rotate-90' : ''}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 border-t border-bundle-cream-dark/60 pt-3">
          {children}
          {onSeeAll && (
            <button
              onClick={onSeeAll}
              data-testid={`stack-card-${id}-see-all`}
              className="w-full mt-3 text-xs font-bold text-bundle-green/70 hover:text-bundle-green hover:bg-bundle-cream/50 py-2 rounded-lg transition-colors"
            >
              See all →
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function LeaderboardRow({ rank, name, score, highlight }) {
  return (
    <div className={`flex items-center gap-2 p-2 rounded-lg ${highlight ? 'bg-bundle-gold/10 border border-bundle-gold/30' : ''}`}>
      <span className={`w-6 text-center font-black text-sm ${rank === 1 ? 'text-bundle-gold-dark' : rank === 2 ? 'text-gray-500' : rank === 3 ? 'text-amber-700' : 'text-gray-400'}`}>
        {rank}
      </span>
      <span className={`flex-1 text-sm truncate ${highlight ? 'font-bold text-bundle-green' : 'text-gray-700'}`}>{name}</span>
      <span className="text-sm font-bold text-bundle-green">{score}</span>
    </div>
  );
}

function StatTile({ label, value, icon: Icon, accent }) {
  return (
    <div className={`p-3 rounded-xl ${accent ? 'bg-bundle-gold/10 border border-bundle-gold/20' : 'bg-bundle-cream/50'}`}>
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className={`w-3 h-3 ${accent ? 'text-bundle-gold-dark' : 'text-bundle-green/60'}`} />
        <span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">{label}</span>
      </div>
      <p className={`text-lg font-black ${accent ? 'text-bundle-gold-dark' : 'text-bundle-green'} leading-none`}>{value}</p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Club Feed composer — Facebook-style entry point.
// Renders header, subtitle, an inline "What's on your mind?" bar, and a row
// of quick actions (Photo / Score / Event / Poll). Each action deep-links to
// the appropriate flow. Tapping the input takes the user to /feed with the
// post modal auto-opened.
// ─────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────
// ClubFeedComposer — TRUE inline Facebook-style composer.
// • Real textarea (no navigation on type)
// • Photo/Video from phone camera or gallery via <input capture="environment">
// • Live preview of selected media
// • Posts directly to /api/social/posts (multipart)
// • Emits `bundle-feed-refresh` event so the mini-feed refreshes on success
// ─────────────────────────────────────────────────────────────────────────
function ClubFeedComposer({ user, onNavigate }) {
  const [text, setText] = React.useState('');
  // Multi-file: files[] + previewUrls[] stay in lockstep. Max 20 items,
  // 100 MB each. Photos and videos can mix in a single gallery post.
  const [files, setFiles] = React.useState([]);
  const [previewUrls, setPreviewUrls] = React.useState([]);
  const [posting, setPosting] = React.useState(false);
  const [expanded, setExpanded] = React.useState(false);
  const textareaRef = React.useRef(null);
  const photoInputRef = React.useRef(null);
  const videoInputRef = React.useRef(null);
  const photoLibraryInputRef = React.useRef(null);
  const videoLibraryInputRef = React.useRef(null);

  const MAX_FILES = 20;
  const MAX_SIZE_BYTES = 100 * 1024 * 1024;
  const [uploadProgress, setUploadProgress] = React.useState(null); // {done, total} while posting

  React.useEffect(() => {
    // Free every object URL when the previews change or the component unmounts
    return () => { previewUrls.forEach((u) => { try { URL.revokeObjectURL(u); } catch {} }); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Compress a phone photo client-side (canvas → JPEG 82%, max 2000px on the
   * long edge). Raw phone photos are 8-15MB each — the ingress + spotty mobile
   * connections start dropping requests around the 4th-6th one. Compressed
   * images land at 300KB-1.5MB, which makes chunked uploads reliably ship.
   * Skips videos (canvas can't touch them) and small already-optimized files.
   */
  const compressImage = async (file) => {
    if (!file.type.startsWith('image/')) return file;
    if (file.size < 500 * 1024) return file; // skip already-small photos
    try {
      const url = URL.createObjectURL(file);
      const img = await new Promise((resolve, reject) => {
        const el = new Image();
        el.onload = () => resolve(el);
        el.onerror = reject;
        el.src = url;
      });
      const maxEdge = 2000;
      let { width, height } = img;
      if (width > maxEdge || height > maxEdge) {
        const ratio = width > height ? maxEdge / width : maxEdge / height;
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }
      const canvas = document.createElement('canvas');
      canvas.width = width; canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);
      URL.revokeObjectURL(url);
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.82));
      if (!blob || blob.size >= file.size) return file; // give up if we made it bigger
      return new File([blob], (file.name || 'photo').replace(/\.\w+$/, '.jpg'), { type: 'image/jpeg' });
    } catch (_e) {
      return file; // any error → ship the raw file
    }
  };

  /**
   * POST one file with automatic retries. Handles the classic "Failed to fetch"
   * transient — mobile networks drop a single request occasionally, so we back
   * off and try again instead of nuking the whole upload.
   */
  const uploadOneWithRetry = async (url, file, tries = 3) => {
    let lastErr = null;
    for (let attempt = 0; attempt < tries; attempt++) {
      try {
        const form = new FormData();
        form.append('media', file);
        const r = await fetch(url, {
          method: 'POST',
          credentials: 'include',
          headers: getAuthHeaders(),
          body: form,
        });
        if (r.ok) return await r.json();
        // 4xx = permanent; don't retry
        if (r.status >= 400 && r.status < 500) {
          const err = await r.json().catch(() => ({}));
          throw new Error(err.detail || `HTTP ${r.status}`);
        }
        lastErr = new Error(`HTTP ${r.status}`);
      } catch (e) {
        lastErr = e;
      }
      // Backoff: 800ms, 1600ms, 3200ms
      if (attempt < tries - 1) {
        await new Promise((res) => setTimeout(res, 800 * (2 ** attempt)));
      }
    }
    throw lastErr || new Error('Upload failed');
  };

  // source: 'camera' opens the phone camera directly (capture="environment").
  // source: 'library' opens the photo/video library or file picker instead.
  const pickFile = (kind, source = 'camera') => {
    if (source === 'library') {
      if (kind === 'video') videoLibraryInputRef.current?.click();
      else photoLibraryInputRef.current?.click();
      return;
    }
    if (kind === 'video') videoInputRef.current?.click();
    else photoInputRef.current?.click();
  };

  const onFileChange = (e) => {
    const picked = Array.from(e.target.files || []);
    if (picked.length === 0) return;

    // Client-side validation: cap count + size, mixed types allowed
    const remaining = MAX_FILES - files.length;
    if (remaining <= 0) {
      window.alert(`You can attach up to ${MAX_FILES} files per post.`);
      e.target.value = '';
      return;
    }
    const accepted = [];
    for (const f of picked.slice(0, remaining)) {
      if (f.size > MAX_SIZE_BYTES) {
        window.alert(`"${f.name}" is over 100MB — skipping.`);
        continue;
      }
      accepted.push(f);
    }
    if (accepted.length === 0) {
      e.target.value = '';
      return;
    }
    if (picked.length > remaining) {
      window.alert(`Attached ${accepted.length} — max ${MAX_FILES} per post.`);
    }

    const newUrls = accepted.map((f) => URL.createObjectURL(f));
    setFiles((prev) => [...prev, ...accepted]);
    setPreviewUrls((prev) => [...prev, ...newUrls]);
    setExpanded(true);
    e.target.value = '';
  };

  const removeFileAt = (idx) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
    setPreviewUrls((prev) => {
      const url = prev[idx];
      if (url) { try { URL.revokeObjectURL(url); } catch {} }
      return prev.filter((_, i) => i !== idx);
    });
  };

  const clearAllFiles = () => {
    previewUrls.forEach((u) => { try { URL.revokeObjectURL(u); } catch {} });
    setFiles([]);
    setPreviewUrls([]);
  };

  const submit = async () => {
    const caption = text.trim();
    if (!caption && files.length === 0) return;
    setPosting(true);
    setUploadProgress(files.length > 0 ? { done: 0, total: files.length } : null);
    let postId = null;
    let failedCount = 0;
    try {
      // 0. Compress every image up-front so subsequent chunk requests stay
      //    small on mobile. Videos and already-small images pass through.
      const prepared = [];
      for (const f of files) {
        prepared.push(await compressImage(f));
      }

      // 1. Create the post with the first file (or caption-only if no media).
      const initForm = new FormData();
      initForm.append('caption', caption);
      if (prepared.length > 0) initForm.append('media', prepared[0]);
      const res = await fetch(`${API}/social/posts`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders(),
        body: initForm,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Could not create post');
      }
      const data = await res.json();
      postId = data.post_id;
      if (prepared.length > 0) setUploadProgress({ done: 1, total: prepared.length });

      // 2. Sequential chunk-upload with retry. A single failure no longer
      //    aborts the whole batch — we just count it and warn at the end.
      if (postId && prepared.length > 1) {
        for (let i = 1; i < prepared.length; i++) {
          try {
            await uploadOneWithRetry(`${API}/social/posts/${postId}/media`, prepared[i]);
          } catch (e) {
            failedCount++;
            console.warn(`Failed to attach file ${i + 1}/${prepared.length}:`, e.message);
          }
          setUploadProgress({ done: i + 1, total: prepared.length });
        }
      }

      // Success path (partial or full)
      setText('');
      clearAllFiles();
      setExpanded(false);
      window.dispatchEvent(new CustomEvent('bundle-feed-refresh'));
      if (failedCount > 0) {
        window.alert(`Post created, but ${failedCount} of ${prepared.length} photos didn't upload. You can Edit the post and try again.`);
      }
    } catch (err) {
      // Init failed OR unexpected exception.
      if (postId) {
        // The post exists but init returned early — treat like partial success.
        setText('');
        clearAllFiles();
        setExpanded(false);
        window.dispatchEvent(new CustomEvent('bundle-feed-refresh'));
        window.alert(`Post created, but only some photos uploaded. ${err.message || ''}`.trim());
      } else {
        window.alert(err.message || 'Could not post. Please try again.');
      }
    } finally {
      setPosting(false);
      setUploadProgress(null);
    }
  };

  const canPost = (text.trim().length > 0 || files.length > 0) && !posting;
  const isVideoAt = (i) => (files[i]?.type || '').startsWith('video/');

  return (
    <div className="mb-6" data-testid="club-feed-composer">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="font-heading font-black text-2xl text-bundle-green leading-tight">Club Feed</h2>
        <button
          type="button"
          onClick={() => onNavigate('/feed')}
          data-testid="club-feed-see-all"
          className="text-xs font-bold text-emerald-700 hover:text-emerald-900"
        >
          See all →
        </button>
      </div>
      <p className="text-sm text-gray-500 mb-3">Stay connected. Share. Compete. Have fun.</p>

      <div className="bg-white rounded-2xl border border-bundle-cream-dark shadow-sm p-3">
        {/* Row 1 — avatar + textarea */}
        <div className="flex items-start gap-3">
          {user?.picture ? (
            <img src={user.picture} alt="" className="w-9 h-9 rounded-full object-cover flex-shrink-0" />
          ) : (
            <div className="w-9 h-9 rounded-full bg-bundle-green/20 flex items-center justify-center font-bold text-bundle-green flex-shrink-0">
              {(user?.name || 'M').charAt(0)}
            </div>
          )}
          <textarea
            ref={textareaRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onFocus={() => setExpanded(true)}
            placeholder={`What's on your mind, ${user?.name?.split(' ')[0] || 'friend'}?`}
            rows={expanded || text || files.length > 0 ? 3 : 1}
            data-testid="composer-textarea"
            className="flex-1 resize-none bg-bundle-cream/70 focus:bg-white rounded-2xl px-4 py-2 text-sm text-gray-800 border border-bundle-cream-dark focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none transition-all placeholder-gray-500"
          />
        </div>

        {/* Media preview — grid for 2+ items, hero for a single item */}
        {previewUrls.length > 0 && (
          <div className="mt-3" data-testid="composer-media-grid">
            <div className={`grid gap-1.5 ${previewUrls.length === 1 ? 'grid-cols-1' : previewUrls.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
              {previewUrls.map((url, i) => (
                <div key={url} className="relative rounded-lg overflow-hidden bg-black/5 aspect-square">
                  {isVideoAt(i) ? (
                    <video src={url} className="w-full h-full object-cover bg-black" data-testid={`composer-media-preview-${i}`} />
                  ) : (
                    <img src={url} alt={`upload ${i+1}`} className="w-full h-full object-cover" data-testid={`composer-media-preview-${i}`} />
                  )}
                  {isVideoAt(i) && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="w-9 h-9 rounded-full bg-black/60 flex items-center justify-center text-white">▶</div>
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => removeFileAt(i)}
                    data-testid={`composer-remove-media-${i}`}
                    className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-black/70 hover:bg-black text-white flex items-center justify-center text-xs shadow-md"
                    aria-label={`Remove file ${i+1}`}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-gray-500 mt-2 flex items-center justify-between">
              <span>{previewUrls.length} of 20 files</span>
              {previewUrls.length >= 2 && (
                <button
                  type="button"
                  onClick={clearAllFiles}
                  className="text-red-500 font-semibold hover:underline"
                  data-testid="composer-clear-all"
                >
                  Remove all
                </button>
              )}
            </p>
          </div>
        )}

        {/* Row 2 — action pills + Post */}
        <div className="grid grid-cols-5 gap-0.5 mt-3 pt-3 border-t border-bundle-cream-dark">
          <button
            type="button"
            onClick={() => pickFile('photo', 'camera')}
            data-testid="composer-photo"
            className="flex flex-col items-center gap-1 py-2 rounded-lg hover:bg-bundle-cream/60 transition-colors"
          >
            <span aria-hidden="true" className="text-xl leading-none">📷</span>
            <span className="text-[10px] font-semibold text-gray-600">Photo</span>
          </button>
          <button
            type="button"
            onClick={() => pickFile('video', 'camera')}
            data-testid="composer-video"
            className="flex flex-col items-center gap-1 py-2 rounded-lg hover:bg-bundle-cream/60 transition-colors"
          >
            <span aria-hidden="true" className="text-xl leading-none">🎥</span>
            <span className="text-[10px] font-semibold text-gray-600">Video</span>
          </button>
          <button
            type="button"
            onClick={() => pickFile('photo', 'library')}
            data-testid="composer-upload"
            className="flex flex-col items-center gap-1 py-2 rounded-lg hover:bg-bundle-cream/60 transition-colors"
          >
            <span aria-hidden="true" className="text-xl leading-none">🖼️</span>
            <span className="text-[10px] font-semibold text-gray-600">Upload</span>
          </button>
          <button
            type="button"
            onClick={() => onNavigate('/dashboard?scan=1')}
            data-testid="composer-score"
            className="flex flex-col items-center gap-1 py-2 rounded-lg hover:bg-bundle-cream/60 transition-colors"
          >
            <span aria-hidden="true" className="text-xl leading-none">⛳</span>
            <span className="text-[10px] font-semibold text-gray-600">Score</span>
          </button>
          <button
            type="button"
            onClick={() => onNavigate('/events?create=1')}
            data-testid="composer-event"
            className="flex flex-col items-center gap-1 py-2 rounded-lg hover:bg-bundle-cream/60 transition-colors"
          >
            <span aria-hidden="true" className="text-xl leading-none">📅</span>
            <span className="text-[10px] font-semibold text-gray-600">Event</span>
          </button>
        </div>

        {(expanded || text || files.length > 0) && (
          <div className="mt-3 pt-3 border-t border-bundle-cream-dark flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => { setText(''); clearAllFiles(); setExpanded(false); }}
              disabled={posting}
              data-testid="composer-cancel"
              className="px-4 py-2 rounded-full text-sm font-semibold text-gray-500 hover:bg-bundle-cream/60 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={!canPost}
              data-testid="composer-post"
              className="px-6 py-2 rounded-full text-sm font-bold text-white bg-bundle-green hover:bg-bundle-green/90 disabled:opacity-40 disabled:cursor-not-allowed shadow-sm"
            >
              {posting
                ? (uploadProgress ? `Uploading ${uploadProgress.done}/${uploadProgress.total}…` : 'Posting…')
                : 'Post'}
            </button>
          </div>
        )}

        {/* Hidden native inputs. `multiple` allows a tournament gallery in one shot.
            capture="environment" opens the phone camera; the library variants show
            the photo picker instead. HEIC/HEIF listed so iPhone photos are selectable. */}
        <input
          ref={photoInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          multiple
          onChange={onFileChange}
          data-testid="composer-photo-input"
          className="hidden"
        />
        <input
          ref={videoInputRef}
          type="file"
          accept="video/*"
          capture="environment"
          multiple
          onChange={onFileChange}
          data-testid="composer-video-input"
          className="hidden"
        />
        <input
          ref={photoLibraryInputRef}
          type="file"
          accept="image/*,video/*,.heic,.heif"
          multiple
          onChange={onFileChange}
          data-testid="composer-photo-library-input"
          className="hidden"
        />
        <input
          ref={videoLibraryInputRef}
          type="file"
          accept="video/*"
          multiple
          onChange={onFileChange}
          data-testid="composer-video-library-input"
          className="hidden"
        />
      </div>
    </div>
  );
}

